import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import type { ApplyBrandResult, BrandColors, BrandSpec, PlaceholderFormat, ThemeColors } from "../../../types";
import { useBrandExtractor } from "../hooks/useBrandExtractor";
import { useBrandApply, type ApplyState } from "../hooks/useBrandApply";
import { useMyBrandTemplates } from "../hooks/useMyBrandTemplates";
import {
  brandMasters, buildBrandSpecFromThemeColors, extractBrand, readMastersFromBuffer, type MasterInfo,
} from "../services/brandExtractor";
import {
  deleteTemplateFile, downloadFileName, getTemplateFile, putTemplateFile, type MyBrandTemplate,
} from "../services/myBrandTemplatesStorage";
import {
  buildPaletteGrid, clampNumber, formatRgbHex, hslPartsToRgb, hsvToRgb, isValidHex, normalizeHex, parseHexRgb,
  rgbToHslParts, rgbToHsv, toHex6, validateColourField, type ColourField,
} from "../../../utils/colourUtils";
import { downloadBytes, getActivePresentationBuffer, goToSlide, PPTX_MIME_TYPE } from "../../../utils/officePptx";
import {
  findMissingFonts, formatFontList, loadFontData, type FontOption,
} from "../../FormatTools/services/fontData";
import { logEvent, logOfficeError } from "../../../../../telemetry";
import { fetchSavedBrandTemplates, type SavedBrandTemplate } from "../services/brandTemplatesApi";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import {
  consumePendingSavedTemplateSelection, subscribePendingSavedTemplateSelection, subscribePreviewedTemplateUnavailable,
} from "../services/pendingSavedTemplateSelection";
import {
  APPLIED_NOTE, ConfirmDialog, CustomiseIcon, MoreMenu, SavedTemplatesTab, SelectedNote, truncateName,
  type SavedTemplateSelectionStatus,
} from "./SavedTemplatesTab";
import styles from "./ApplyBrandPanel.module.css";

type SourceTab = "active" | "upload" | "saved";

/** Advanced Options (BRD FR-47): only Keep rounded corners / shadows / animations are exposed;
 *  the rest stay off or on as before (Fix alignment and Autofit are excluded from this BRD). */
export type AdvancedOpts = CustomiseAdvanced;

const DEFAULT_ADVANCED_OPTIONS: AdvancedOpts = {
  allowShadows: true, isAllowGradients: true, allowRoundedCorners: true, allowAnimations: true,
  compressImages: false, isFixAlignment: false, autofitSlideContent: false,
};

/** Intro message per tab (BRD FR-02). */
const INTRO_MESSAGE: Record<SourceTab, { title: string; text: string }> = {
  saved: { title: "Make this presentation match a brand", text: "Pick a brand below, then press Apply Brand. That's it." },
  active: { title: "Match your slides to this deck's theme", text: "Check the theme below, then press Apply Brand. That's it." },
  upload: { title: "Use a template from your files", text: "Upload a .potx or .pptx below, then press Apply Brand. That's it." },
};

/** Tooltip shown on a disabled Apply button (BRD FR-55). */
const DISABLED_REASON: Record<SourceTab, string> = {
  saved: "Pick a template in this tab to continue",
  active: "No active theme to apply. Choose a brand template to continue",
  upload: "Upload a template to continue",
};

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const HIGHLIGHT_MS = 4000;


/** What is currently selected (BR-01: only one source at a time, and Apply only uses
 *  a selection that belongs to the tab on screen). */
interface ActiveSource {
  readonly tab: SourceTab;
  readonly kind: CustomiseSourceKind;
  /** Unique across sources: org:<id>, local:<id>, theme:<masterId>, upload:<uploadId>:<masterId>. */
  readonly key: string;
  readonly name: string;
  readonly localId?: string;
}

interface Remembered {
  readonly active: ActiveSource;
  readonly brand: BrandSpec;
}

interface PendingCustomise {
  readonly key: string;
  readonly missing?: readonly StyleSection[];
}

interface ApplyBrandPanelProps {
  readonly onBack?: () => void;
}

/** Usage telemetry for the apply, upload, save, rename and delete actions (BRD NFR-10). */
function logApplyBrandUsage(reason: string): void {
  logEvent("connector.action.invoke", "usage", { connectorSystemName: "deckorate", reason });
}

function swatchesOf(brand: BrandSpec): string[] {
  return [
    brand.colors.accent1, brand.colors.accent2, brand.colors.accent3,
    brand.colors.accent4, brand.colors.accent5, brand.colors.accent6,
    brand.colors.dk1, brand.colors.dk2,
  ].map((c) => normalizeHex(c)).filter((c): c is string => c !== null);
}

function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`; // NOSONAR typescript:S2245
}

function fileTypeLabel(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const known: Record<string, string> = {
    pdf: "PDF", doc: "Word document", docx: "Word document", xls: "Excel workbook", xlsx: "Excel workbook",
    txt: "text file", png: "PNG image", jpg: "JPEG image", jpeg: "JPEG image", zip: "ZIP archive",
  };
  return known[ext] ?? (ext ? `.${ext} file` : "file of an unknown type");
}

/** A name that doesn't clash with `taken`, adding " (2)", " (3)" … before the extension. */
function nextFreeName(name: string, taken: readonly string[]): string {
  const lower = new Set(taken.map((t) => t.toLowerCase()));
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let n = 2;
  while (lower.has(`${stem} (${n})${ext}`.toLowerCase())) n++;
  return `${stem} (${n})${ext}`;
}

/** An OLE compound file (legacy .ppt or an encrypted/password-protected package) — not a zip. */
function looksPasswordProtected(buffer: ArrayBuffer): boolean {
  const head = new Uint8Array(buffer, 0, Math.min(8, buffer.byteLength));
  return head.length >= 4 && head[0] === 0xd0 && head[1] === 0xcf && head[2] === 0x11 && head[3] === 0xe0;
}

/** The saved-template id behind an "org:" or "local:" source key, or null for any other key. */
function savedIdFromKey(key: string | null): string | null {
  if (key?.startsWith("org:")) return key.slice("org:".length);
  if (key?.startsWith("local:")) return key.slice("local:".length);
  return null;
}

const SAVED_STATUS: Partial<Record<string, SavedTemplateSelectionStatus>> = {
  extracting: "extracting", done: "ready", error: "error",
};

/** The Saved Templates tab's view of the current selection's extraction. */
function savedTemplateStatus(active: ActiveSource | null, extractStatus: string): SavedTemplateSelectionStatus {
  if (active?.tab !== "saved") return "idle";
  return SAVED_STATUS[extractStatus] ?? "idle";
}

/** The intro note shows until dismissed, and only while its tab has nothing to show yet. */
function isIntroVisible(
  tab: SourceTab, dismissed: ReadonlySet<SourceTab>, active: ActiveSource | null, deck: DeckLoadState, uploadCount: number,
): boolean {
  if (dismissed.has(tab)) return false;
  if (tab === "saved") return active?.tab !== "saved";
  if (tab === "active") return deck.status !== "ready" || brandMasters(deck.masters).length > 0;
  return uploadCount === 0;
}

interface SelectionView {
  /** Saved Templates: the selected brand or Your Template id. */
  readonly savedId: string | null;
  /** Saved Templates: the brand template with Customise changes for the next apply. */
  readonly customisedOrgId: string | null;
  readonly appliedSavedId: string | null;
  readonly appliedMasterId: string | null;
  /** Upload: the selected upload/theme key. */
  readonly uploadKey: string | null;
}

/** The per-tab selection and Applied ids the source tabs render from. */
function selectionView(active: ActiveSource | null, appliedKey: string | null, hasEdits: (key: string) => boolean): SelectionView {
  let savedId: string | null = null;
  if (active?.tab === "saved") savedId = active.localId ?? active.key.slice("org:".length);
  const customisedOrgId = active?.kind === "brand" && hasEdits(active.key) ? active.key.slice("org:".length) : null;
  return {
    savedId,
    customisedOrgId,
    appliedSavedId: savedIdFromKey(appliedKey),
    appliedMasterId: appliedKey?.startsWith("theme:") ? appliedKey.slice("theme:".length) : null,
    uploadKey: active?.tab === "upload" ? active.key : null,
  };
}

/** Warns that a brand template's fonts aren't installed on this device (shown above Apply). */
function OrgFontWarning({ fonts }: Readonly<{ fonts: readonly string[] }>) {
  if (fonts.length === 0) return null;
  const plural = fonts.length > 1;
  return (
    <BrandAlert tone="warning">
      <b>{fonts.join(", ")}</b> {plural ? "aren't" : "isn't"} installed on this device.
      This brand template uses {plural ? "them" : "it"}. PowerPoint will show a substitute until it&rsquo;s
      installed. Ask your IT team to install it.
    </BrandAlert>
  );
}

export function ApplyBrandPanel({ onBack }: Readonly<ApplyBrandPanelProps>) {
  const [sourceTab, setSourceTab] = useState<SourceTab>("saved");
  const [scope, setScope] = useState<Scope>("slide");
  const [saveTarget, setSaveTarget] = useState<SaveTarget>("current");
  const [dismissedIntros, setDismissedIntros] = useState<ReadonlySet<SourceTab>>(new Set());
  const [notice, setNotice] = useState<BarNotice | null>(null);
  // Set only while the apply bar shows the result of "Apply to These Slides" (FR-63, AC3).
  const [reapplyTotal, setReapplyTotal] = useState<number | null>(null);
  const [highlightYours, setHighlightYours] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [active, setActive] = useState<ActiveSource | null>(null);
  // Customise changes per source key: brand template changes last for one apply (BR-02);
  // deck theme / upload / Your Template changes are kept and used for every later apply (BR-03).
  const [edits, setEdits] = useState<Record<string, { brand: BrandSpec; advanced: AdvancedOpts }>>({});
  const [addedStyles, setAddedStyles] = useState<Record<string, readonly StyleSection[]>>({});
  const [customising, setCustomising] = useState<{ missing?: readonly StyleSection[] } | null>(null);
  const [pendingCustomise, setPendingCustomise] = useState<PendingCustomise | null>(null);
  const [appliedKey, setAppliedKey] = useState<string | null>(null);
  const tabMemoryRef = useRef<Partial<Record<SourceTab, Remembered>>>({});

  // This Deck's Theme
  const [deck, setDeck] = useState<DeckLoadState>({ status: "loading" });
  const deckBufferRef = useRef<ArrayBuffer | null>(null);
  // The deck is read once and reused when switching back to the tab; it is read again
  // only after an apply has changed it.
  const deckStaleRef = useRef(false);
  const [selectedMasterId, setSelectedMasterId] = useState<string | null>(null);
  const [removeOthers, setRemoveOthers] = useState(false);
  const [savedKeys, setSavedKeys] = useState<ReadonlySet<string>>(new Set());
  const [freshlyRead, setFreshlyRead] = useState<string | null>(null);

  // Upload
  const [uploads, setUploads] = useState<readonly UploadEntry[]>([]);
  const [reading, setReading] = useState<string | null>(null);
  const [problem, setProblem] = useState<UploadProblem | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicatePrompt | null>(null);
  const [renameRequestId, setRenameRequestId] = useState<string | null>(null);
  const lastFileRef = useRef<File | null>(null);
  const pendingDuplicateRef = useRef<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const brandCacheRef = useRef<Map<string, BrandSpec>>(new Map());

  const { state: extractState, extractFromBuffer, extractFromUrls, setDirectBrand, reset: resetExtract } = useBrandExtractor();
  const { state: applyState, apply, cancel: cancelApply, reset: resetApply } = useBrandApply();
  const {
    templates: myTemplates, save: saveMyTemplate, remove: removeMyTemplate,
    rename: renameMyTemplate, update: updateMyTemplate,
  } = useMyBrandTemplates();

  const http = useDeckorateHttp();
  const baseBrand: BrandSpec | null = extractState.status === "done" ? extractState.brand : null;
  const activeEdit = active ? edits[active.key] : undefined;
  const finalBrand = activeEdit?.brand ?? baseBrand;
  const finalAdvanced = activeEdit?.advanced ?? DEFAULT_ADVANCED_OPTIONS;
  const onThisTab = active !== null && active.tab === sourceTab;

  const clearFeedback = useCallback(() => {
    setNotice(null);
    setReapplyTotal(null);
    resetApply();
  }, [resetApply]);

  // ── Selection ──────────────────────────────────────────────────────────────

  const selectSavedTemplate = useCallback((template: SavedBrandTemplate) => {
    // Prefer building the brand synchronously from themeColors; only fall
    // back to downloading + parsing the .pptx when there's no themeColors.
    if (!template.themeColors && template.sourceFileUrlCandidates.length === 0) return;
    clearFeedback();
    setActive({ tab: "saved", kind: "brand", key: `org:${template.id}`, name: template.name });
    if (template.themeColors) {
      setDirectBrand(buildBrandSpecFromThemeColors(template.themeColors), template.name);
    } else {
      extractFromUrls(template.sourceFileUrlCandidates, template.name).catch((e) => logOfficeError(e, "apply-brand-extract-saved"));
    }
  }, [clearFeedback, extractFromUrls, setDirectBrand]);

  const selectMyTemplate = useCallback((template: MyBrandTemplate) => {
    clearFeedback();
    setActive({ tab: "saved", kind: "yours", key: `local:${template.id}`, name: template.name, localId: template.id });
    setDirectBrand(template.brand, template.name);
  }, [clearFeedback, setDirectBrand]);

  const selectMaster = useCallback((master: MasterInfo, fresh = true) => {
    const buffer = deckBufferRef.current;
    if (!buffer) return;
    clearFeedback();
    setSelectedMasterId(master.id);
    setActive({ tab: "active", kind: "theme", key: `theme:${master.id}`, name: master.name });
    if (fresh) setFreshlyRead(`theme:${master.id}`);
    extractFromBuffer(buffer, master.name, { themePath: master.themePath, masterPath: master.masterPath })
      .catch((e) => logOfficeError(e, "apply-brand-extract-active"));
  }, [clearFeedback, extractFromBuffer]);

  const selectUpload = useCallback((upload: UploadEntry, theme: { id: string; name: string; themePath: string; masterPath: string }) => {
    clearFeedback();
    const key = uploadKey(upload.id, theme.id);
    setActive({ tab: "upload", kind: "upload", key, name: upload.themes.length > 1 ? theme.name : upload.name });
    setFreshlyRead(key);
    const cached = brandCacheRef.current.get(key);
    if (cached) {
      setDirectBrand(cached, upload.name);
      return;
    }
    extractFromBuffer(upload.buffer, upload.name, { themePath: theme.themePath, masterPath: theme.masterPath })
      .catch((e) => logOfficeError(e, "apply-brand-extract-upload"));
  }, [clearFeedback, extractFromBuffer, setDirectBrand]);

  // Remember each extracted brand so the cache can answer later selections / saves.
  useEffect(() => {
    if (active?.kind === "upload" && baseBrand) brandCacheRef.current.set(active.key, baseBrand);
  }, [active, baseBrand]);

  // The Asset Library's Brand Templates tab routes a pick here instead of
  // doing anything itself — see pendingSavedTemplateSelection.ts. Applied
  // once on mount (a later, ordinary visit to Apply Brand must not replay a
  // stale pick, which is exactly what consuming — not just reading — achieves)
  // and again live via the subscription below, for a pick made (e.g. Switch
  // Template from the Brand Templates preview) while this panel is already
  // open and mounted, where the mount effect alone would never re-fire.
  const applyPendingSavedTemplateSelection = useCallback(() => {
    const pending = consumePendingSavedTemplateSelection();
    if (!pending) return;
    setSourceTab("saved");
    if (pending.kind === "org") selectSavedTemplate(pending.template);
    else selectMyTemplate(pending.template);
    // FR-21: confirms the Preview's Use/Switch Template choice landed here —
    // select* above clears any notice via clearFeedback(), so this runs after.
    setNotice({ tone: "success", text: `${pending.template.name} loaded into Apply Brand. Press Apply Brand when ready.` });
  }, [selectSavedTemplate, selectMyTemplate]);

  useEffect(() => {
    applyPendingSavedTemplateSelection();
  }, [applyPendingSavedTemplateSelection]);

  useEffect(
    () => subscribePendingSavedTemplateSelection(applyPendingSavedTemplateSelection),
    [applyPendingSavedTemplateSelection],
  );

  // ── This Deck's Theme: read the open deck's masters ─────────────────────────

  const loadDeck = useCallback(async () => {
    setDeck({ status: "loading" });
    deckStaleRef.current = false;
    try {
      const buffer = await getActivePresentationBuffer();
      deckBufferRef.current = buffer;
      const masters = readMastersFromBuffer(buffer);
      if (masters === null) {
        setDeck({ status: "error", message: "The file couldn't be read. It may be corrupt or password-protected." });
        return;
      }
      setDeck({ status: "ready", masters });
    } catch (e) {
      logOfficeError(e, "apply-brand-read-deck");
      setDeck({ status: "error", message: e instanceof Error ? e.message : "Something went wrong reading the presentation." });
    }
  }, []);

  // Opening This Deck's Theme always selects the deck's theme (FR-05): the
  // masters are read, then the remembered (or first brand) master is selected.
  const deckReady = deck.status === "ready" ? deck.masters : null;
  useEffect(() => {
    if (sourceTab !== "active" || !deckReady) return;
    if (active?.tab === "active") return;
    const brand = brandMasters(deckReady);
    const pick = brand.find((m) => m.id === selectedMasterId) ?? brand[0];
    if (pick) selectMaster(pick);
  }, [sourceTab, deckReady, active?.tab]);

  // ── Tabs ───────────────────────────────────────────────────────────────────

  const handleTabChange = useCallback((tab: SourceTab) => {
    if (tab === sourceTab) return;
    clearFeedback();
    setCustomising(null);
    setPendingCustomise(null);
    // Remember what this tab had selected so returning restores it (FR-05).
    if (active?.tab === sourceTab && baseBrand) {
      tabMemoryRef.current[sourceTab] = { active, brand: baseBrand };
    }
    resetExtract();
    setActive(null);
    setSourceTab(tab);

    if (tab === "active") {
      if (deck.status === "ready" && deckBufferRef.current && !deckStaleRef.current) {
        // Already read: restore the last selection without reading the deck again.
        const remembered = tabMemoryRef.current.active;
        if (remembered) {
          setActive(remembered.active);
          setDirectBrand(remembered.brand, remembered.active.name);
        }
        return;
      }
      loadDeck().catch((e) => logOfficeError(e, "apply-brand-read-deck"));
      return;
    }
    const remembered = tabMemoryRef.current[tab];
    if (!remembered) return;
    // An upload removed since is not restored (its memory is dropped on removal).
    setActive(remembered.active);
    setDirectBrand(remembered.brand, remembered.active.name);
  }, [sourceTab, active, baseBrand, deck.status, clearFeedback, resetExtract, setDirectBrand, loadDeck]);

  const goToYourTemplates = useCallback(() => {
    handleTabChange("saved");
    setHighlightYours(true);
    setTimeout(() => setHighlightYours(false), HIGHLIGHT_MS);
  }, [handleTabChange]);

  // ── Customise ──────────────────────────────────────────────────────────────

  // Choosing Customise selects the item first, then opens Customise once its brand is ready.
  useEffect(() => {
    if (!pendingCustomise || !active || active.key !== pendingCustomise.key || !baseBrand) return;
    setCustomising({ missing: pendingCustomise.missing });
    setPendingCustomise(null);
  }, [pendingCustomise, active, baseBrand]);

  // The library changed and the selected brand template is gone: clear the selection (EC-05).
  const clearUnavailableSelection = useCallback(() => {
    delete tabMemoryRef.current.saved;
    setActive((a) => (a?.tab === "saved" && a.kind === "brand" ? null : a));
    resetExtract();
  }, [resetExtract]);

  // The Brand Templates preview (opened from the eye icon) noticed the
  // organisation withdrew the template it was showing (EC-08): by the time
  // the preview closes, the pane has already cleared the selection and shown
  // the same "no longer available" message EC-04 uses at apply time.
  useEffect(() => subscribePreviewedTemplateUnavailable(() => {
    clearUnavailableSelection();
    setNotice({
      tone: "error",
      title: "That brand template is no longer available.",
      text: "Your organisation updated the library, so nothing was changed. Choose another one instead.",
    });
  }), [clearUnavailableSelection]);

  const customiseSavedTemplate = useCallback((sel: { kind: "org"; template: SavedBrandTemplate } | { kind: "local"; template: MyBrandTemplate }) => {
    if (sel.kind === "org") {
      selectSavedTemplate(sel.template);
      setPendingCustomise({ key: `org:${sel.template.id}` });
    } else {
      selectMyTemplate(sel.template);
      setPendingCustomise({ key: `local:${sel.template.id}` });
    }
  }, [selectSavedTemplate, selectMyTemplate]);

  const stillMissing: StyleSection[] = useMemo(() => {
    if (!active || active.kind === "brand" || !finalBrand) return [];
    const added = addedStyles[active.key] ?? [];
    return (finalBrand.missingStyles ?? []).filter((s) => !added.includes(s));
  }, [active, finalBrand, addedStyles]);

  const openAddMissing = useCallback(() => {
    if (active) setCustomising({ missing: stillMissing });
  }, [active, stillMissing]);

  const finishCustomise = useCallback((brand: BrandSpec, advanced: AdvancedOpts, added: StyleSection[], mode: "use" | "save") => {
    if (!active) return;
    logApplyBrandUsage(mode === "use" ? "apply-brand-customise-used" : "apply-brand-customise-saved");
    const allAdded = [...new Set([...(addedStyles[active.key] ?? []), ...added])];
    const nextBrand: BrandSpec = { ...brand, missingStyles: (brand.missingStyles ?? []).filter((s) => !allAdded.includes(s)) };
    setAddedStyles((prev) => ({ ...prev, [active.key]: allAdded }));
    setCustomising(null);
    setNotice(null);

    if (mode === "use") {
      setEdits((prev) => ({ ...prev, [active.key]: { brand: nextBrand, advanced } }));
      setNotice({ tone: "info", text: "Changes will be used for this apply." });
      return;
    }
    if (active.kind === "yours" && active.localId) {
      const updated = updateMyTemplate(active.localId, nextBrand, swatchesOf(nextBrand));
      if (updated) {
        setDirectBrand(updated.brand, updated.name);
        setEdits((prev) => ({ ...prev, [active.key]: { brand: updated.brand, advanced } }));
        // FR-50, AC2: "Changes saved to <name>." for a saved Your Template.
        setNotice({ tone: "success", text: `Changes saved to ${active.name}.` });
      }
      return;
    }
    setEdits((prev) => ({ ...prev, [active.key]: { brand: nextBrand, advanced } }));
    // FR-50, AC3: "Changes saved. They'll be used every time you apply <name>." for deck theme / upload.
    setNotice({ tone: "info", text: `Changes saved. They'll be used every time you apply ${active.name}.` });
  }, [active, addedStyles, updateMyTemplate, setDirectBrand]);

  // ── Your Templates actions ─────────────────────────────────────────────────

  const handleRenameMyTemplate = useCallback((id: string, name: string) => {
    const outcome = renameMyTemplate(id, name);
    if (outcome === "renamed") {
      logApplyBrandUsage("apply-brand-template-renamed");
      setNotice({ tone: "success", text: `Renamed to ${name.trim()}.` });
      if (active?.localId === id) setActive((a) => (a ? { ...a, name: name.trim() } : a));
    }
    return outcome;
  }, [renameMyTemplate, active]);

  const handleDeleteMyTemplate = useCallback((id: string) => {
    const deleted = myTemplates.find((t) => t.id === id);
    removeMyTemplate(id);
    logApplyBrandUsage("apply-brand-template-deleted");
    if (deleted) setNotice({ tone: "success", text: `${deleted.name} deleted from Your Templates.` });
    deleteTemplateFile(id).catch((e) => logOfficeError(e, "apply-brand-delete-template-file"));
    const remembered = tabMemoryRef.current.saved?.active;
    if (remembered?.localId === id) delete tabMemoryRef.current.saved;
    if (active?.localId === id) {
      resetExtract();
      resetApply();
      setActive(null);
    }
  }, [myTemplates, removeMyTemplate, active, resetExtract, resetApply]);

  const handleDownloadMyTemplate = useCallback((template: MyBrandTemplate) => {
    getTemplateFile(template.id)
      .then((bytes) => {
        if (!bytes) {
          setNotice({ tone: "warning", text: `${template.name} can't be downloaded: its original file wasn't kept on this device.` });
          return;
        }
        downloadBytes(bytes, downloadFileName(template.name), PPTX_MIME_TYPE);
      })
      .catch((e) => logOfficeError(e, "apply-brand-download-template"));
  }, []);

  /** Saves a source to Your Templates, handling the 3-template limit (EC-22). */
  const saveToYourTemplates = useCallback((
    name: string, brand: BrandSpec, markKey: string, fileBytes: ArrayBuffer | null,
  ) => {
    const uniqueName = nextFreeNameForTemplates(name, myTemplates.map((t) => t.name));
    const { outcome, entry } = saveMyTemplate(uniqueName, brand, swatchesOf(brand));
    if (outcome === "limit") {
      setNotice({
        tone: "warning",
        text: `You have reached the limit of 3 saved templates. To save ${name}, delete one of your saved templates first.`,
        actions: [{ label: "Go to Your Templates", onClick: goToYourTemplates }],
      });
      return;
    }
    if (outcome !== "saved" || !entry) return;
    logApplyBrandUsage("apply-brand-template-saved");
    setSavedKeys((prev) => new Set(prev).add(markKey));
    setNotice({ tone: "success", text: `Saved. ${entry.name} is now listed under Templates · Your Templates.` });
    if (fileBytes) putTemplateFile(entry.id, fileBytes).catch((e) => logOfficeError(e, "apply-brand-store-template-file"));
  }, [myTemplates, saveMyTemplate, goToYourTemplates]);

  const saveMaster = useCallback(async (master: MasterInfo) => {
    const buffer = deckBufferRef.current;
    if (!buffer) return;
    const key = `theme:${master.id}`;
    const brand = edits[key]?.brand ?? (active?.key === key ? baseBrand : null)
      ?? await extractBrand(buffer, { themePath: master.themePath, masterPath: master.masterPath });
    saveToYourTemplates(`${master.name} theme`, brand, key, buffer);
  }, [edits, active, baseBrand, saveToYourTemplates]);

  const saveUpload = useCallback(async (upload: UploadEntry, theme: MasterInfo) => {
    const key = uploadKey(upload.id, theme.id);
    const cached = edits[`upload:${key}`]?.brand ?? brandCacheRef.current.get(key);
    const brand = cached ?? await extractBrand(upload.buffer, { themePath: theme.themePath, masterPath: theme.masterPath });
    brandCacheRef.current.set(key, brand);
    saveToYourTemplates(upload.themes.length > 1 ? theme.name : upload.name, brand, key, upload.buffer);
  }, [edits, saveToYourTemplates]);

  // ── Upload ─────────────────────────────────────────────────────────────────

  const readUpload = useCallback(async (file: File, mode: "add" | "replace" | "keep-both") => {
    lastFileRef.current = file;
    setProblem(null);
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setProblem({ tone: "error", title: "Connection lost.", text: "Nothing was uploaded. Check your connection, then try again.", isRetry: true });
      return;
    }
    setReading(file.name);
    try {
      const buffer = await file.arrayBuffer();
      if (looksPasswordProtected(buffer)) {
        setProblem({
          tone: "error", title: "This file can't be read.",
          text: "It's read-only or password-protected, so nothing was uploaded. Make it editable in PowerPoint, then try again.",
          isRetry: true,
        });
        return;
      }
      const masters = readMastersFromBuffer(buffer);
      if (masters === null) {
        setProblem({ tone: "error", title: "Couldn't upload the file.", text: "Something went wrong, so nothing was uploaded. Try again.", isRetry: true });
        return;
      }
      const themes = brandMasters(masters);
      if (themes.length === 0) {
        setProblem({
          tone: "warning", title: "This file doesn't have an active theme.",
          text: "It uses the default Office Theme, so nothing was uploaded. Choose a file with a brand theme instead.", isRetry: false,
        });
        return;
      }
      const first = themes[0]!;
      const brand = await extractBrand(buffer, { themePath: first.themePath, masterPath: first.masterPath });
      const taken = uploads.map((u) => u.name);
      let name = file.name;
      if (mode === "keep-both") name = nextFreeName(file.name, taken);
      const entry: UploadEntry = { id: newId(), name, buffer, themes, swatches: swatchesOf(brand) };
      const replaced = mode === "replace" ? uploads.find((u) => u.name === file.name) : undefined;
      if (replaced) {
        for (const t of replaced.themes) brandCacheRef.current.delete(uploadKey(replaced.id, t.id));
      }
      setUploads((prev) => (replaced ? prev.map((u) => (u.id === replaced.id ? entry : u)) : [...prev, entry]));
      brandCacheRef.current.set(uploadKey(entry.id, first.id), brand);
      clearFeedback();
      logApplyBrandUsage("apply-brand-upload-read");
      // FR-29 / EC-21: "<file> uploaded." on a fresh or Keep Both upload, "<name> replaced." on Replace.
      setNotice({ tone: "success", text: replaced ? `${name} replaced.` : `${name} uploaded.` });
      setActive({ tab: "upload", kind: "upload", key: uploadKey(entry.id, first.id), name: themes.length > 1 ? first.name : name });
      setFreshlyRead(uploadKey(entry.id, first.id));
      setDirectBrand(brand, name);
      if (mode === "keep-both") setRenameRequestId(entry.id);
    } catch (e) {
      logOfficeError(e, "apply-brand-upload");
      setProblem({ tone: "error", title: "Couldn't upload the file.", text: "Something went wrong, so nothing was uploaded. Try again.", isRetry: true });
    } finally {
      setReading(null);
    }
  }, [uploads, clearFeedback, setDirectBrand]);

  const handleFiles = useCallback((files: FileList | File[] | null) => {
    const file = files?.[0];
    // Closing the file dialog without choosing a file changes nothing (EC-20).
    if (!file) return;
    setProblem(null);
    if (!/\.(pptx|potx)$/i.test(file.name)) {
      setProblem({
        tone: "error", title: "That file type isn't supported.",
        text: `It's a ${fileTypeLabel(file.name)}, so nothing was uploaded. Choose a .pptx or .potx file instead.`, isRetry: false,
      });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      const mb = Number((file.size / (1024 * 1024)).toFixed(1));
      setProblem({
        tone: "error", title: "That file is too large.",
        text: `It's ${mb} MB and the limit is 50 MB, so nothing was uploaded. Remove large videos or images in PowerPoint, then try again.`,
        isRetry: false,
      });
      return;
    }
    if (uploads.some((u) => u.name === file.name)) {
      pendingDuplicateRef.current = file;
      setDuplicate({ name: file.name });
      return;
    }
    readUpload(file, "add").catch((e) => logOfficeError(e, "apply-brand-upload"));
  }, [uploads, readUpload]);

  const handleDuplicateChoice = useCallback((choice: "cancel" | "keep-both" | "replace") => {
    const file = pendingDuplicateRef.current;
    pendingDuplicateRef.current = null;
    setDuplicate(null);
    if (choice === "cancel" || !file) return;
    readUpload(file, choice).catch((e) => logOfficeError(e, "apply-brand-upload"));
  }, [readUpload]);

  const handleRemoveUpload = useCallback((upload: UploadEntry) => {
    const wasSaved = upload.themes.some((t) => savedKeys.has(uploadKey(upload.id, t.id)));
    setUploads((prev) => prev.filter((u) => u.id !== upload.id));
    for (const t of upload.themes) brandCacheRef.current.delete(uploadKey(upload.id, t.id));
    const remembered = tabMemoryRef.current.upload?.active;
    if (remembered?.key.startsWith(`upload:${upload.id}`) || remembered?.key.startsWith(`${upload.id}:`)) delete tabMemoryRef.current.upload;
    if (active?.kind === "upload" && active.key.startsWith(`${upload.id}:`)) {
      resetExtract();
      resetApply();
      setActive(null);
    }
    // No confirmation. A saved copy stays in Your Templates (BR-06).
    setNotice({
      tone: "info",
      text: wasSaved ? `${upload.name} removed from uploads. It's still in Your Templates.` : `${upload.name} removed from uploads.`,
    });
  }, [active, savedKeys, resetExtract, resetApply]);

  const handleRenameUpload = useCallback((id: string, name: string): "ok" | "duplicate" => {
    if (uploads.some((u) => u.id !== id && u.name.toLowerCase() === name.toLowerCase())) return "duplicate";
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, name } : u)));
    setActive((a) => (a?.kind === "upload" && a.key.startsWith(`${id}:`) && uploads.find((u) => u.id === id)?.themes.length === 1 ? { ...a, name } : a));
    return "ok";
  }, [uploads]);

  const customiseUpload = useCallback((upload: UploadEntry, theme: MasterInfo) => {
    selectUpload(upload, theme);
    setPendingCustomise({ key: uploadKey(upload.id, theme.id) });
  }, [selectUpload]);

  const customiseMaster = useCallback((master: MasterInfo) => {
    selectMaster(master, false);
    setPendingCustomise({ key: `theme:${master.id}` });
  }, [selectMaster]);

  // ── Apply ──────────────────────────────────────────────────────────────────

  const handleApply = useCallback(async () => {
    if (!finalBrand || !active) return;
    clearFeedback();
    // A brand template the organisation withdrew since it was picked can't be applied (BR-11, EC-04).
    // If the library can't be reached to check, the apply goes ahead as before.
    if (active.kind === "brand") {
      const stillListed = await fetchSavedBrandTemplates(http)
        .then((list) => list.some((t) => t.id === active.key.slice("org:".length)))
        .catch(() => true);
      if (!stillListed) {
        clearUnavailableSelection();
        setNotice({
          tone: "error",
          title: "That brand template is no longer available.",
          text: "Your organisation updated the library, so nothing was changed. Choose another one instead.",
        });
        return;
      }
    }
    const extras = {
      destination: scope === "deck" && saveTarget === "new" ? ("copy" as const) : ("update" as const),
      moveToMaster: scope === "deck" && removeOthers && active.kind === "theme" ? active.name : undefined,
    };
    apply(finalBrand, { scope, ...finalAdvanced }, extras).catch((e) => logOfficeError(e, "apply-brand-apply"));
  }, [finalBrand, active, scope, saveTarget, removeOthers, finalAdvanced, apply, clearFeedback, http, clearUnavailableSelection]);

  // FR-63: re-apply brand to a specific set of slides (EC-31 skipped / EC-32 edited after apply).
  const handleApplyToSlides = useCallback((indices: readonly number[], total: number) => {
    if (!finalBrand || !active || indices.length === 0) return;
    clearFeedback();
    setReapplyTotal(total);
    const extras = { destination: "update" as const };
    apply(finalBrand, { scope: "deck", ...finalAdvanced, slideIndices: indices }, extras)
      .catch((e) => logOfficeError(e, "apply-brand-apply-to-slides"));
  }, [finalBrand, active, finalAdvanced, apply, clearFeedback]);

  // After a successful apply: a brand template's Customise changes were for that apply only
  // (FR-65), and a whole-presentation update marks the source Applied (FR-08).
  const lastHandledResult = useRef<unknown>(null);
  useEffect(() => {
    if (applyState.status !== "done" || lastHandledResult.current === applyState.result || !active) return;
    lastHandledResult.current = applyState.result;
    logApplyBrandUsage(`apply-brand-applied-${scope}`);
    deckStaleRef.current = true;
    delete tabMemoryRef.current.active;
    if (sourceTab === "active") loadDeck().catch((e) => logOfficeError(e, "apply-brand-read-deck"));
    if (active.kind === "brand") {
      setEdits((prev) => {
        if (!(active.key in prev)) return prev;
        const next = { ...prev };
        delete next[active.key];
        return next;
      });
    }
    if (scope === "deck" && saveTarget === "current") setAppliedKey(active.key);
  }, [applyState, active, scope, saveTarget, sourceTab, loadDeck]);

  // Cancelled and failed applies are logged too (the failure itself is logged by the hook).
  useEffect(() => {
    if (applyState.status === "cancelled") logApplyBrandUsage("apply-brand-cancelled");
    else if (applyState.status === "error") logApplyBrandUsage(`apply-brand-failed-${applyState.kind}`);
  }, [applyState]);

  const handleScopeChange = useCallback((next: Scope) => {
    setScope(next);
    // Changing the scope resets the destination (FR-53); This Slide unticks Remove other masters (FR-25).
    setSaveTarget("current");
    if (next === "slide") setRemoveOthers(false);
  }, []);

  const isBusy = extractState.status === "extracting" || applyState.status === "applying" || reading !== null;
  const applyDisabled = !finalBrand || !onThisTab || isBusy;

  // ── Derived UI data ────────────────────────────────────────────────────────

  const sourceFonts = useMemo(
    () => (finalBrand ? findMissingFonts([finalBrand.fonts.titleFont, finalBrand.fonts.bodyFont]) : []),
    [finalBrand],
  );

  const addedForActive = active ? addedStyles[active.key] ?? [] : [];
  const allAdded = active !== null && active.kind !== "brand" && addedForActive.length > 0 && stillMissing.length === 0;

  const keptStylesText = useMemo(() => {
    if (!active || active.kind === "brand" || stillMissing.length === 0) return null;
    const list = listStyles(stillMissing);
    return `${list.charAt(0).toUpperCase()}${list.slice(1)} kept as they were. ${active.name} doesn't include them.`;
  }, [active, stillMissing]);

  const skippedActions = useMemo(() => {
    const lines: string[] = [];
    if (finalAdvanced.allowRoundedCorners) lines.push("Rounded corners kept as they were (Keep rounded corners is on)");
    if (finalAdvanced.allowShadows) lines.push("Shadows kept as they were (Keep shadows is on)");
    if (finalAdvanced.allowAnimations) lines.push("Animations kept as they were (Keep animations is on)");
    return lines;
  }, [finalAdvanced]);

  const renderNotes = useCallback((name: string, key: string) => {
    if (active?.key !== key || !baseBrand) return null;
    return (
      <>
        <SourceNotes
          sourceName={name}
          stillMissing={stillMissing}
          allAdded={allAdded}
          tableMissing={!(finalBrand?.tableDetected ?? true)}
          missingFonts={sourceFonts}
          showCaptured={freshlyRead === key}
          onAddMissing={openAddMissing}
        />
        <SelectedNote name={name} ready onReview={() => setCustomising({})} />
      </>
    );
  }, [active, baseBrand, stillMissing, allAdded, finalBrand, sourceFonts, freshlyRead, openAddMissing]);

  // This Deck's Theme shows only the read/missing/font notes (FR-23) — no
  // "will be applied / Review & Customise" line; Customise is reached from the
  // card's ⋯ menu, as in the mock. The source name is "this presentation" for a
  // single master, the master's own name when the deck has several.
  const renderThemeNotes = useCallback((master: MasterInfo, sourceName: string, focus: "styles" | "fonts" | "captured") => {
    const key = `theme:${master.id}`;
    if (active?.key !== key || !baseBrand) return null;
    return (
      <SourceNotes
        sourceName={sourceName}
        stillMissing={stillMissing}
        allAdded={allAdded}
        tableMissing={!(finalBrand?.tableDetected ?? true)}
        missingFonts={sourceFonts}
        showCaptured={freshlyRead === key}
        onAddMissing={openAddMissing}
        focus={focus}
      />
    );
  }, [active, baseBrand, stillMissing, allAdded, finalBrand, sourceFonts, freshlyRead, openAddMissing]);

  const savedStatus = savedTemplateStatus(active, extractState.status);
  const selection = selectionView(active, appliedKey, (key) => Boolean(edits[key]));
  const orgFontWarning = active?.kind === "brand" ? sourceFonts : [];

  // ── Render ─────────────────────────────────────────────────────────────────

  if (settingsOpen) return <SettingsScreen onBack={() => setSettingsOpen(false)} />;

  if (customising && baseBrand && active) {
    return (
      <CustomiseScreen
        brand={finalBrand ?? baseBrand}
        advanced={finalAdvanced}
        sourceKind={active.kind}
        sourceName={active.name}
        missingSections={customising.missing}
        onUseForThisApply={(b, adv, added) => finishCustomise(b, adv, added, "use")}
        onSaveChanges={(b, adv, added) => finishCustomise(b, adv, added, "save")}
        onDone={() => setCustomising(null)}
        onDiscard={() => {
          setCustomising(null);
          setNotice({
            tone: "info",
            text: active.kind === "yours" ? "Changes discarded. The saved template is unchanged." : "Changes discarded.",
          });
        }}
      />
    );
  }

  const introVisible = isIntroVisible(sourceTab, dismissedIntros, active, deck, uploads.length);

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader
          title="Apply Brand"
          subtitle="Deckorate"
          onBack={onBack}
          backLabel="Back"
          actions={
            <button
              type="button"
              className={styles.settingsBtn}
              aria-label="Apply Brand settings"
              title="Apply Brand settings"
              onClick={() => setSettingsOpen(true)}
            >
              <svg aria-hidden="true" viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="10" cy="10" r="2.5" />
                <path d="M10 3v2M10 15v2M3 10h2M15 10h2M5 5l1.4 1.4M13.6 13.6L15 15M5 15l1.4-1.4M13.6 6.4L15 5" />
              </svg>
            </button>
          }
        />

        {introVisible && (
          <output className={styles.introNote}>
            <span className={styles.introText}>
              <span className={styles.introTitle}>{INTRO_MESSAGE[sourceTab].title}</span>
              <span>{INTRO_MESSAGE[sourceTab].text}</span>
            </span>
            <button
              type="button"
              className={styles.introClose}
              aria-label="Close intro message"
              onClick={() => setDismissedIntros((prev) => new Set(prev).add(sourceTab))}
            >
              ✕
            </button>
          </output>
        )}

        <fieldset className={styles.sourceTabs} aria-label="Brand source">
          {([["saved", "Templates"], ["active", "This Deck’s Theme"], ["upload", "Upload"]] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={sourceTab === key}
              className={`${styles.sourceTabBtn} ${sourceTab === key ? styles.sourceTabBtnActive : ""}`}
              onClick={() => handleTabChange(key)}
            >
              {label}
            </button>
          ))}
        </fieldset>

        {sourceTab === "saved" && (
          <SavedTemplatesTab
            selectedId={selection.savedId}
            disabled={isBusy}
            selectedStatus={savedStatus}
            customisedId={selection.customisedOrgId}
            appliedId={selection.appliedSavedId}
            isHighlightYours={highlightYours}
            onSelect={selectSavedTemplate}
            onSelectionUnavailable={clearUnavailableSelection}
            onCustomise={customiseSavedTemplate}
            myTemplates={myTemplates}
            onSelectMyTemplate={selectMyTemplate}
            onRenameMyTemplate={handleRenameMyTemplate}
            onDownloadMyTemplate={handleDownloadMyTemplate}
            onDeleteMyTemplate={handleDeleteMyTemplate}
          />
        )}

        {sourceTab === "saved" && active?.tab === "saved" && active.kind === "yours" && (
          <SourceNotes
            sourceName={active.name}
            stillMissing={stillMissing}
            allAdded={allAdded}
            tableMissing={!(finalBrand?.tableDetected ?? true)}
            missingFonts={sourceFonts}
            showCaptured={false}
            onAddMissing={openAddMissing}
          />
        )}

        {sourceTab === "active" && (
          <ThemeTab
            load={deck}
            selectedMasterId={active?.tab === "active" ? selectedMasterId : null}
            scope={scope}
            removeOthers={removeOthers}
            onRemoveOthersChange={setRemoveOthers}
            savedMasterIds={new Set([...savedKeys].filter((k) => k.startsWith("theme:")).map((k) => k.slice(6)))}
            appliedMasterId={selection.appliedMasterId}
            onSelectMaster={(m) => selectMaster(m)}
            onCustomise={customiseMaster}
            onSave={(m) => { saveMaster(m).catch((e) => logOfficeError(e, "apply-brand-save-theme")); }}
            onBrowseTemplates={() => handleTabChange("saved")}
            renderNotes={renderThemeNotes}
            disabled={isBusy}
            hasMissingStyles={active?.tab === "active" && (stillMissing.length > 0 || !(finalBrand?.tableDetected ?? true))}
            hasMissingFonts={active?.tab === "active" && sourceFonts.length > 0}
          />
        )}

        {sourceTab === "upload" && (
          <UploadTab
            uploads={uploads}
            selectedKey={selection.uploadKey}
            appliedKey={appliedKey}
            reading={reading}
            problem={problem}
            duplicate={duplicate}
            savedKeys={savedKeys}
            renameRequestId={renameRequestId}
            fileInputRef={fileInputRef}
            onFiles={handleFiles}
            onSelect={selectUpload}
            onCustomise={customiseUpload}
            onSave={(u, t) => { saveUpload(u, t).catch((e) => logOfficeError(e, "apply-brand-save-upload")); }}
            onRemove={handleRemoveUpload}
            onRename={handleRenameUpload}
            onRenameDone={() => setRenameRequestId(null)}
            onRetry={() => {
              const file = lastFileRef.current;
              if (file) readUpload(file, "add").catch((e) => logOfficeError(e, "apply-brand-upload"));
            }}
            onDismissProblem={() => setProblem(null)}
            onDuplicateChoice={handleDuplicateChoice}
            renderNotes={(u, t) => renderNotes(u.themes.length > 1 ? t.name : u.name, uploadKey(u.id, t.id))}
          />
        )}

        {extractState.status === "error" && sourceTab !== "saved" && (
          <BrandAlert tone="error" title="Extraction failed:">
            {extractState.message}
          </BrandAlert>
        )}


      <ApplyBar
        scope={scope}
        onScopeChange={handleScopeChange}
        saveTarget={saveTarget}
        onSaveTargetChange={setSaveTarget}
        sourceName={active?.name ?? ""}
        applyState={applyState}
        onCancel={cancelApply}
        onApply={() => { handleApply().catch((e) => logOfficeError(e, "apply-brand-apply")); }}
        disabled={applyDisabled}
        disabledReason={DISABLED_REASON[sourceTab]}
        notice={notice}
        onCloseNotice={() => setNotice(null)}
        keptStylesText={keptStylesText}
        skippedActions={skippedActions}
        aboveButton={<OrgFontWarning fonts={orgFontWarning} />}
        onApplyToSlides={handleApplyToSlides}
        reapplyTotal={reapplyTotal}
      />
      </div>
    </div>
  );
}

/** Same as nextFreeName but a clash only counts for a name without an extension clash on templates. */
function nextFreeNameForTemplates(name: string, taken: readonly string[]): string {
  return taken.some((t) => t.toLowerCase() === name.toLowerCase()) ? nextFreeName(name, taken) : name;
}

// ─── Alerts ────────────────────────────────────────────────────────────────

export type AlertTone = "error" | "warning" | "info" | "success";

interface BrandAlertProps {
  readonly tone: AlertTone;
  readonly title?: string;
  readonly children?: React.ReactNode;
  readonly actions?: readonly { label: string; onClick: () => void }[];
  /** Alerts in the apply bar and upload area can be closed with ✕ (BRD section 8). */
  readonly onClose?: () => void;
}

const ALERT_CLASS: Record<AlertTone, string> = {
  error: "brandAlertError", warning: "brandAlertWarning", info: "brandAlertInfo", success: "brandAlertSuccess",
};

/** AiVA alert glyphs as inline SVGs (matching the mock exactly) — replaces the
 *  earlier emoji so the icons render consistently across platforms. */
function AlertIcon({ tone }: Readonly<{ tone: AlertTone }>) {
  const common = {
    "aria-hidden": true as const, viewBox: "0 0 20 20", width: 15, height: 15, fill: "none",
    stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
  };
  switch (tone) {
    case "error":
      return (<svg {...common}><circle cx="10" cy="10" r="8" /><path d="M7 7l6 6" /><path d="M13 7l-6 6" /></svg>);
    case "warning":
      return (
        <svg {...common}>
          <path d="M10 3L2 17h16z" /><path d="M10 8v4" />
          <circle cx="10" cy="14.5" r="0.75" fill="currentColor" stroke="none" />
        </svg>
      );
    case "success":
      return (<svg {...common}><circle cx="10" cy="10" r="8" /><path d="M7 10l2 2 4-4" /></svg>);
    default: // info
      return (
        <svg {...common}>
          <circle cx="10" cy="10" r="8" /><path d="M10 9.5v4.5" />
          <circle cx="10" cy="6.3" r="0.9" fill="currentColor" stroke="none" />
        </svg>
      );
  }
}

/** Floppy-disk Save glyph for the per-card Save button (matching the mock). */
function SaveIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 2h8l4 4v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" />
      <path d="M13 2v4H7V2" />
      <rect x="6" y="11" width="8" height="5" rx="1" />
    </svg>
  );
}

/** Up-arrow used on the upload drop area and the "Upload Template" button. */
function UploadArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 3v14" />
      <path d="M6 7l4-4 4 4" />
    </svg>
  );
}

/** Clock next to "available until PowerPoint closes" on the uploaded list header. */
function ClockIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="10" r="8" />
      <path d="M10 6v4l2.5 2" />
    </svg>
  );
}

/** AiVA alert pattern (BRD section 8): status background, 4 px left border in
 *  the status colour, icon, title and body. Errors use role "alert"; warnings,
 *  info and success messages render as <output> (implicit role "status"). */
export function BrandAlert({ tone, title, children, actions = [], onClose }: Readonly<BrandAlertProps>) {
  const className = `${styles.brandAlert} ${styles[ALERT_CLASS[tone]]}`;
  const content = (
    <>
      <span className={styles.brandAlertIcon} aria-hidden="true"><AlertIcon tone={tone} /></span>
      <div className={styles.brandAlertContent}>
        {title && <div className={styles.brandAlertTitle}>{title}</div>}
        {children && <div className={styles.brandAlertBody}>{children}</div>}
        {actions.length > 0 && (
          <div className={styles.brandAlertActions}>
            {actions.map((a) => (
              <button key={a.label} type="button" className={styles.brandAlertActionBtn} onClick={a.onClick}>
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {onClose && (
        <button type="button" className={styles.brandAlertClose} aria-label="Dismiss message" title="Close" onClick={onClose}>
          <svg aria-hidden="true" viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 4L4 16M4 4l12 12" />
          </svg>
        </button>
      )}
    </>
  );
  return tone === "error" ? (
    <div className={className} role="alert">{content}</div>
  ) : (
    <output className={className}>{content}</output>
  );
}

// ─── Source notes ──────────────────────────────────────────────────────────

const SECTION_LABEL: Record<StyleSection, string> = {
  title: "title", body: "body", subtitle: "subtitle", footnote: "footnote",
};

/** "title", "title and body", "title, body and footnote". */
export function listStyles(sections: readonly StyleSection[]): string {
  const labels = sections.map((s) => SECTION_LABEL[s]);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

const CAPTURED_VISIBLE_MS = 4000;

interface SourceNotesProps {
  /** Name shown in messages: the master, upload or template. */
  readonly sourceName: string;
  /** Sections still missing from the source (EC-09). */
  readonly stillMissing: readonly StyleSection[];
  /** True once every originally-missing section was added in Customise. */
  readonly allAdded: boolean;
  /** The source has no table style (EC-09). It can't be added: tables aren't customised. */
  readonly tableMissing: boolean;
  /** Fonts this source uses that aren't installed on the device (EC-11). */
  readonly missingFonts: readonly string[];
  /** Show the "captured" confirmation (FR-23) — only for a freshly read source. */
  readonly showCaptured: boolean;
  readonly onAddMissing: () => void;
  /** Which notes to render. "all" (default) shows every applicable note; the
   *  This Deck's Theme filter narrows to one kind — "styles" (missing-styles /
   *  added / captured), "fonts" (font-not-installed), or "captured" (the read
   *  confirmation / added success only, never the missing-styles warning). */
  readonly focus?: "all" | "styles" | "fonts" | "captured";
}

/** The missing-styles / "styles captured" block (EC-09, FR-23). */
function StyleNote({
  sourceName, stillMissing, allAdded, tableMissing, captured, onAddMissing, isDismissed, onClose,
}: Readonly<{
  sourceName: string; stillMissing: readonly StyleSection[]; allAdded: boolean;
  tableMissing: boolean; captured: boolean; onAddMissing: () => void;
  /** The missing-styles warning was closed with its ✕ (as in the mock). */
  isDismissed: boolean; onClose: () => void;
}>) {
  if (stillMissing.length > 0 || tableMissing) {
    if (isDismissed) return null;
    return (
      <BrandAlert
        tone="warning"
        title={`Some styles are missing from ${sourceName}`}
        actions={stillMissing.length > 0 ? [{ label: "Add Missing Styles", onClick: onAddMissing }] : []}
        onClose={onClose}
      >
        {stillMissing.length > 0 && <>No {listStyles(stillMissing)} styles found. Add them, or slides keep their current look.</>}
        {tableMissing && <> Table style isn&rsquo;t included. Tables keep their current look.</>}
      </BrandAlert>
    );
  }
  if (allAdded) return <BrandAlert tone="success">Missing styles added in Customise</BrandAlert>;
  if (captured) return <BrandAlert tone="success">Colours, fonts and styles captured</BrandAlert>;
  return null;
}

/** Notes shown under a selected deck theme / upload / Your Template: the read
 *  confirmation (hides after 4 seconds), the missing-styles warning with Add
 *  Missing Styles, and the font-not-installed warning (BRD FR-23, EC-09, EC-11). */
export function SourceNotes({
  sourceName, stillMissing, allAdded, tableMissing, missingFonts, showCaptured, onAddMissing, focus = "all",
}: Readonly<SourceNotesProps>) {
  const [captured, setCaptured] = useState(showCaptured);

  useEffect(() => {
    if (!showCaptured) return;
    setCaptured(true);
    const id = setTimeout(() => setCaptured(false), CAPTURED_VISIBLE_MS);
    return () => clearTimeout(id);
  }, [showCaptured, sourceName]);

  // The warnings' ✕ only hides them for this source; picking another source shows them again.
  const [stylesClosed, setStylesClosed] = useState(false);
  const [fontsClosed, setFontsClosed] = useState(false);
  useEffect(() => {
    setStylesClosed(false);
    setFontsClosed(false);
  }, [sourceName]);

  const styleNote = focus === "fonts" ? null : (
    <StyleNote
      sourceName={sourceName}
      // "captured" narrows the styles block to the success notes only — the
      // missing-styles warning lives in its own filtered view.
      stillMissing={focus === "captured" ? [] : stillMissing}
      allAdded={allAdded}
      tableMissing={focus === "captured" ? false : tableMissing}
      captured={captured}
      onAddMissing={onAddMissing}
      isDismissed={stylesClosed}
      onClose={() => setStylesClosed(true)}
    />
  );
  const fontNote = (focus === "styles" || focus === "captured") || missingFonts.length === 0 || fontsClosed ? null : (
    <BrandAlert
      tone="warning"
      title={`${formatFontList(missingFonts)} ${missingFonts.length > 1 ? "aren't" : "isn't"} installed on this device`}
      onClose={() => setFontsClosed(true)}
    >
      {sourceName.charAt(0).toUpperCase() + sourceName.slice(1)} uses{" "}
      {missingFonts.length > 1 ? "these fonts" : "this font"}. PowerPoint will show a substitute until{" "}
      {missingFonts.length > 1 ? "they’re" : "it’s"} installed, or choose another font in Customise.
    </BrandAlert>
  );

  return (
    <>
      {styleNote}
      {fontNote}
    </>
  );
}

// ─── Colour picker ─────────────────────────────────────────────────────────

interface EyeDropperLike { open: (opts?: { signal?: AbortSignal }) => Promise<{ sRGBHex: string }> }
type EyeDropperCtor = new () => EyeDropperLike;

function getEyeDropper(): EyeDropperCtor | null {
  const ctor = (globalThis as { EyeDropper?: EyeDropperCtor }).EyeDropper;
  return ctor ?? null;
}

/** True when the host can sample a colour from the screen (BRD NFR-07: only
 *  offered wherever the eyedropper is supported). */
export function eyedropperSupported(): boolean {
  return getEyeDropper() !== null;
}

/** Runs the screen eyedropper, resolving to a 6-digit hex or null when
 *  cancelled (Esc) or unsupported. */
export async function pickColourFromScreen(): Promise<string | null> {
  const Ctor = getEyeDropper();
  if (!Ctor) return null;
  try {
    const result = await new Ctor().open();
    return toHex6(result.sRGBHex);
  } catch (error) {
    // Pressing Esc rejects with an AbortError: an expected cancel, not a failure.
    if (!(error instanceof DOMException && error.name === "AbortError")) logOfficeError(error, "apply-brand-eyedropper");
    return null;
  }
}

/** Eyedropper icon for the colour picker (matches the mock's pencil-style tool), distinct
 *  from the font colour gallery's own text-labelled "Eyedropper" link (FR-42). */
function EyedropperIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19.5 4.5a2.1 2.1 0 0 1 0 3l-9 9-4 1 1-4 9-9a2.1 2.1 0 0 1 3 0Z" />
      <path d="M14 7l3 3" />
    </svg>
  );
}

/** The dropdown chevron inside each theme/secondary colour slot (matches the mock). */
function SlotChevron() {
  return (
    <svg className={styles.czSlotChevron} aria-hidden="true" viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 8l5 5 5-5" />
    </svg>
  );
}

export type PickerMode = "edit" | "add" | "existing-secondary";

interface ColourPickerProps {
  readonly title: string;
  readonly value: string;
  readonly mode?: PickerMode;
  readonly onApply: (hex: string) => void;
  readonly onCancel: () => void;
  readonly onRemove?: () => void;
}

type Texts = Record<ColourField, string>;

function textsFromHex(hex: string): Texts {
  const rgb = parseHexRgb(hex);
  const hsl = rgbToHslParts(rgb);
  return {
    hex: `#${hex}`,
    r: String(rgb.r), g: String(rgb.g), b: String(rgb.b),
    h: String(Math.round(hsl.h)), s: String(Math.round(hsl.s * 100)), l: String(Math.round(hsl.l * 100)),
  };
}

/** The three value-field views the picker cycles through (BRD FR-41's "RGB /
 *  HSL / HEX value fields") — one shown at a time, switched with the toggle
 *  next to the fields, as in the mock. */
const FIELD_GROUPS: ReadonlyArray<{ key: "rgb" | "hsl" | "hex"; items: ReadonlyArray<{ field: ColourField; label: string }> }> = [
  { key: "rgb", items: [{ field: "r", label: "R" }, { field: "g", label: "G" }, { field: "b", label: "B" }] },
  { key: "hsl", items: [{ field: "h", label: "H" }, { field: "s", label: "S" }, { field: "l", label: "L" }] },
  { key: "hex", items: [{ field: "hex", label: "HEX" }] },
];

/** The up/down toggle that cycles the picker between RGB, HSL and HEX fields. */
function FieldModeToggleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="8 9 12 5 16 9" />
      <polyline points="8 15 12 19 16 15" />
    </svg>
  );
}

/** The custom colour picker (BRD FR-41): saturation/brightness area, hue
 *  slider, eyedropper, preview dot and RGB / HSL / HEX fields (one view at a
 *  time, cycled with the up/down toggle), with Cancel and Apply (Add for a
 *  new secondary colour, Remove for an existing one). Apply is blocked while
 *  any field holds an invalid value (EC-26). */
export function ColourPicker({ title, value, mode = "edit", onApply, onCancel, onRemove }: Readonly<ColourPickerProps>) {
  const initial = isValidHex(value) ? toHex6(value) : "808080";
  const [hsv, setHsv] = useState(() => rgbToHsv(parseHexRgb(initial)));
  const hex = formatRgbHex(hsvToRgb(hsv));
  const [texts, setTexts] = useState<Texts>(() => textsFromHex(initial));
  const [errors, setErrors] = useState<Partial<Record<ColourField, string>>>({});
  const [picking, setPicking] = useState(false);
  const [fieldMode, setFieldMode] = useState<"rgb" | "hsl" | "hex">("rgb");
  const cardRef = useRef<HTMLDialogElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const saturationRef = useRef<HTMLInputElement>(null);
  const firstFieldRef = useRef<HTMLButtonElement>(null);

  const setFromHsv = useCallback((next: typeof hsv) => {
    setHsv(next);
    setTexts(textsFromHex(formatRgbHex(hsvToRgb(next))));
    setErrors({});
  }, []);

  useEffect(() => {
    (firstFieldRef.current ?? saturationRef.current)?.focus();
  }, []);

  // Escape closes the picker without letting the key reach the panel. A native
  // listener keeps keyboard handlers off the non-interactive <dialog> element.
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    };
    card.addEventListener("keydown", onKeyDown);
    return () => card.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  const moveInArea = (clientX: number, clientY: number) => {
    const rect = areaRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    const s = clampNumber((clientX - rect.left) / rect.width, 0, 1);
    const v = clampNumber(1 - (clientY - rect.top) / rect.height, 0, 1);
    setFromHsv({ ...hsv, s, v });
  };

  const onFieldChange = (field: ColourField, raw: string) => {
    setTexts((t) => ({ ...t, [field]: raw }));
    const error = validateColourField(field, raw);
    setErrors((e) => ({ ...e, [field]: error ?? undefined }));
    if (error) return;
    const t = { ...texts, [field]: raw };
    if (field === "hex") {
      setHsv(rgbToHsv(parseHexRgb(toHex6(raw))));
      setTexts(textsFromHex(toHex6(raw)));
    } else if (field === "r" || field === "g" || field === "b") {
      const rgb = { r: Number(t.r), g: Number(t.g), b: Number(t.b) };
      setHsv(rgbToHsv(rgb));
      setTexts({ ...textsFromHex(formatRgbHex(rgb)), [field]: raw });
    } else {
      const rgb = hslPartsToRgb({ h: Number(t.h), s: Number(t.s) / 100, l: Number(t.l) / 100 });
      setHsv(rgbToHsv(rgb));
      setTexts({ ...textsFromHex(formatRgbHex(rgb)), [field]: raw });
    }
  };

  // Leaving an invalid field restores the last valid value (EC-26).
  const onFieldBlur = (field: ColourField) => {
    if (!validateColourField(field, texts[field])) return;
    setTexts(textsFromHex(hex));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const hasError = Object.values(errors).some(Boolean);

  const pick = async () => {
    setPicking(true);
    const picked = await pickColourFromScreen();
    setPicking(false);
    if (picked) setFromHsv(rgbToHsv(parseHexRgb(picked)));
  };

  const applyLabel = mode === "add" ? "Add" : "Apply";
  // fieldMode's type is exactly the union of FIELD_GROUPS' keys, so a match always exists.
  const activeGroup = FIELD_GROUPS.find((g) => g.key === fieldMode)!;
  const nextFieldMode = FIELD_GROUPS[(FIELD_GROUPS.findIndex((g) => g.key === fieldMode) + 1) % FIELD_GROUPS.length]!.key;
  const cycleFieldMode = () => setFieldMode(nextFieldMode);
  const firstError = (Object.values(errors).find(Boolean) as string | undefined) ?? null;

  return (
    <dialog ref={cardRef} open className={styles.czPickerCard} aria-label={title}>
      <div className={styles.czPickerTitle}>{title}</div>
      {/* The 2D area is pointer-only; keyboard and screen-reader users drive the
          same values through the two visually hidden native range inputs. */}
      <div
        ref={areaRef}
        className={styles.czPickerArea}
        style={{ backgroundColor: `#${formatRgbHex(hsvToRgb({ h: hsv.h, s: 1, v: 1 }))}` }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture?.(e.pointerId);
          moveInArea(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) moveInArea(e.clientX, e.clientY);
        }}
      >
        <div className={styles.czPickerAreaWhite} aria-hidden="true" />
        <div className={styles.czPickerAreaBlack} aria-hidden="true" />
        <span
          className={styles.czPickerThumb}
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
          aria-hidden="true"
        />
        <input
          ref={saturationRef}
          type="range"
          className={styles.srOnly}
          min={0}
          max={100}
          value={Math.round(hsv.s * 100)}
          aria-label="Saturation"
          aria-valuetext={`${Math.round(hsv.s * 100)}%`}
          onChange={(e) => setFromHsv({ ...hsv, s: Number(e.target.value) / 100 })}
        />
        <input
          type="range"
          className={styles.srOnly}
          min={0}
          max={100}
          value={Math.round(hsv.v * 100)}
          aria-label="Brightness"
          aria-valuetext={`${Math.round(hsv.v * 100)}%`}
          onChange={(e) => setFromHsv({ ...hsv, v: Number(e.target.value) / 100 })}
        />
      </div>

      <div className={styles.czPickerRow}>
        {eyedropperSupported() && (
          <button ref={firstFieldRef} type="button" className={styles.czPickerEyedropperBtn} onClick={pick} aria-label="Eyedropper" title="Eyedropper">
            <EyedropperIcon />
          </button>
        )}
        {/* Decorative: the same colour is announced through the HEX field below. */}
        <span className={styles.czPickerDot} style={{ backgroundColor: `#${hex}` }} aria-hidden="true" />
        <input
          type="range"
          className={styles.czPickerHue}
          min={0}
          max={360}
          value={Math.round(hsv.h)}
          aria-label="Hue"
          onChange={(e) => setFromHsv({ ...hsv, h: Number(e.target.value) })}
        />
      </div>
      {picking && <output className={styles.czPickerHint}>Click anywhere to pick a colour. Press Esc to cancel.</output>}

      <div className={styles.czPickerFieldBlock}>
        <div className={styles.czPickerFieldRow}>
          {activeGroup.items.map(({ field, label }) => (
            <input
              key={field}
              type="text"
              inputMode={field === "hex" ? undefined : "numeric"}
              className={field === "hex" ? styles.czPickerInput : styles.czPickerInputSmall}
              value={texts[field]}
              aria-label={`${fieldMode.toUpperCase()} ${label}`}
              aria-invalid={Boolean(errors[field])}
              onChange={(e) => onFieldChange(field, e.target.value)}
              onBlur={() => onFieldBlur(field)}
            />
          ))}
          <button
            type="button"
            className={styles.czPickerModeToggle}
            onClick={cycleFieldMode}
            aria-label={`Switch to ${nextFieldMode.toUpperCase()} values`}
            title="Switch value format"
          >
            <FieldModeToggleIcon />
          </button>
        </div>
        <div className={styles.czPickerFieldLabelRow}>
          {activeGroup.items.map(({ field, label }) => (
            <span key={field} className={styles.czPickerFieldLabel}>{label}</span>
          ))}
        </div>
      </div>
      {firstError && <div className={styles.czFieldError} role="alert">{firstError}</div>}

      <div className={styles.czPickerActions}>
        {mode === "existing-secondary" && onRemove && (
          <button type="button" className={styles.czSmallBtn} onClick={onRemove}>Remove</button>
        )}
        <button type="button" className={styles.czPickerCancelBtn} onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className={styles.czPrimarySmallBtn}
          disabled={hasError}
          aria-disabled={hasError}
          onClick={() => onApply(hex)}
        >
          {applyLabel}
        </button>
      </div>
    </dialog>
  );
}

// ─── Font combobox ─────────────────────────────────────────────────────────

interface FontComboboxProps {
  readonly label: string;
  readonly value: string;
  readonly fonts: readonly FontOption[];
  /** The brand's theme fonts, offered as the "Theme Fonts" group. */
  readonly themeFonts: readonly { label: string; name: string }[];
  readonly onChange: (name: string) => void;
}

interface Row {
  readonly id: string;
  readonly name: string;
  readonly hint?: string;
}

/** Heading / Body font field (BRD FR-43): type-to-filter combobox with a
 *  Theme Fonts group and an All Fonts group, each font shown in its own
 *  typeface and the current one ticked. Arrow keys, Enter, Esc and Tab are
 *  supported. When nothing matches, "Use “<text>” anyway" accepts the typed
 *  name; clearing the field restores the previous font. */
export function FontCombobox({ label, value, fonts, themeFonts, onChange }: Readonly<FontComboboxProps>) {
  const baseId = useId();
  const listId = `${baseId}-list`;
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const query = typed ? text.trim().toLowerCase() : "";
  const themeRows: Row[] = useMemo(
    () =>
      themeFonts
        .filter((t) => t.name && (!query || t.name.toLowerCase().includes(query)))
        .map((t) => ({ id: `${baseId}-theme-${t.label}`, name: t.name, hint: t.label })),
    [themeFonts, query, baseId],
  );
  const allRows: Row[] = useMemo(
    () =>
      fonts
        .filter((f) => !query || f.name.toLowerCase().includes(query))
        .map((f, i) => ({ id: `${baseId}-all-${i}`, name: f.name })),
    [fonts, query, baseId],
  );
  const rows = [...themeRows, ...allRows];
  const noMatch = query !== "" && rows.length === 0;

  function close() {
    setOpen(false);
    setTyped(false);
    // Closing without choosing (including after clearing the field) restores the current font.
    setText(value);
  }

  const choose = (name: string) => {
    setText(name);
    setOpen(false);
    setTyped(false);
    if (name !== value) onChange(name);
    inputRef.current?.focus();
  };

  const moveActive = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!open) {
      setOpen(true);
      return;
    }
    if (rows.length === 0) return;
    const step = e.key === "ArrowDown" ? 1 : -1;
    setActive((a) => (a + step + rows.length) % rows.length);
  };

  const chooseActive = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    e.preventDefault();
    const row = rows[active];
    if (row) choose(row.name);
    else if (noMatch) choose(text.trim());
  };

  const dismiss = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    e.preventDefault();
    e.stopPropagation();
    close();
  };

  const keyHandlers: Partial<Record<string, (e: React.KeyboardEvent<HTMLInputElement>) => void>> = {
    ArrowDown: moveActive,
    ArrowUp: moveActive,
    Enter: chooseActive,
    Escape: dismiss,
    Tab: () => {
      if (open) close();
    },
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => keyHandlers[e.key]?.(e);

  const activeRow = rows[active];
  return (
    <div className={styles.czFieldRow} ref={wrapRef}>
      <label htmlFor={`${baseId}-input`} className={styles.czFieldLabel}>{label}</label>
      <div className={styles.czComboWrap}>
        <input
          ref={inputRef}
          id={`${baseId}-input`}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && activeRow ? activeRow.id : undefined}
          className={styles.czComboInput}
          style={{ fontFamily: `"${value}", sans-serif` }}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setTyped(true);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {open && (
          // ARIA editable-combobox pattern: a native <select>/<datalist> can't filter as you type or preview fonts.
          <div id={listId} role="listbox" aria-label={`${label} fonts`} className={styles.czComboList} /* NOSONAR */>
            {noMatch ? (
              <div className={styles.czComboEmpty}>
                <div>No fonts match &ldquo;{text.trim()}&rdquo;</div>
                <button type="button" className={styles.czLinkBtn} onClick={() => choose(text.trim())}>
                  Use &ldquo;{text.trim()}&rdquo; anyway
                </button>
              </div>
            ) : (
              <>
                {themeRows.length > 0 && <div className={styles.czComboGroup}>Theme Fonts</div>}
                {themeRows.map((row) => (
                  <FontRow key={row.id} row={row} rowIndex={rows.indexOf(row)} active={rows[active] === row} current={row.name === value} onChoose={choose} />
                ))}
                {allRows.length > 0 && <div className={styles.czComboGroup}>All Fonts</div>}
                {allRows.map((row) => (
                  <FontRow key={row.id} row={row} rowIndex={rows.indexOf(row)} active={rows[active] === row} current={row.name === value} onChoose={choose} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FontRow({ row, rowIndex, active, current, onChoose }: Readonly<{
  row: Row; rowIndex: number; active: boolean; current: boolean; onChoose: (name: string) => void;
}>) {
  return (
    // Focus stays in the combobox input, which points here via aria-activedescendant.
    <div // NOSONAR
      id={row.id}
      role="option"
      aria-selected={current}
      data-index={rowIndex}
      className={`${styles.czComboOption} ${active ? styles.czComboOptionActive : ""}`}
      style={{ fontFamily: `"${row.name}", sans-serif` }}
      onMouseDown={(e) => {
        e.preventDefault();
        onChoose(row.name);
      }}
    >
      <span className={styles.czComboTick} aria-hidden="true">{current ? "✓" : ""}</span>
      <span className={styles.czComboName}>{row.name}</span>
      {row.hint && <span className={styles.czComboHint}>{row.hint}</span>}
    </div>
  );
}

// ─── Font colour gallery ───────────────────────────────────────────────────

const STANDARD_COLOURS = [
  "C00000", "FF0000", "FFC000", "FFFF00", "92D050", "00B050", "00B0F0", "0070C0", "002060", "7030A0",
];

interface FontColourGalleryProps {
  readonly colors: BrandColors;
  readonly secondary: readonly string[];
  readonly value: string;
  readonly onPick: (hex: string) => void;
  readonly onEyedropper: () => void;
  readonly onMoreColours: () => void;
}

/** Font colour gallery (BRD FR-46): Theme Colours (10 swatches plus 5 rows of
 *  lighter and darker shades), Custom Colours (the brand's secondary colours),
 *  Standard Colours (10), Eyedropper and More Colours…. */
export function FontColourGallery({ colors, secondary, value, onPick, onEyedropper, onMoreColours }: Readonly<FontColourGalleryProps>) {
  const theme: ThemeColors = {
    dk1: colors.dk1, lt1: colors.lt1, dk2: colors.dk2, lt2: colors.lt2,
    accent1: colors.accent1, accent2: colors.accent2, accent3: colors.accent3,
    accent4: colors.accent4, accent5: colors.accent5, accent6: colors.accent6,
    hlink: colors.hlink ?? "0563C1", folHlink: colors.folHlink ?? "954F72",
  };
  const grid = buildPaletteGrid(theme);
  const current = value.toUpperCase();

  const swatch = (hex: string, label: string) => (
    <button
      key={`${label}-${hex}`}
      type="button"
      className={`${styles.czGallerySwatch} ${hex.toUpperCase() === current ? styles.czGallerySwatchOn : ""}`}
      style={{ backgroundColor: `#${hex}` }}
      title={`${label} #${hex}`}
      aria-label={`${label} #${hex}`}
      aria-pressed={hex.toUpperCase() === current}
      onClick={() => onPick(hex.toUpperCase())}
    />
  );

  return (
    <fieldset className={styles.czGallery} aria-label="Font colour">
      <div className={styles.czGalleryLabel}>Theme Colours</div>
      <div className={styles.czGalleryGrid}>
        {/* The first row of the grid is the 10 base swatches; the rest are shades. */}
        {grid.flatMap((row, ri) => row.map((cell) => swatch(cell.hex, ri === 0 ? cell.key : cell.label)))}
      </div>
      {secondary.length > 0 && (
        <>
          <div className={styles.czGalleryLabel}>Custom Colours</div>
          <div className={styles.czGalleryRow}>{secondary.map((hex) => swatch(hex, "Custom"))}</div>
        </>
      )}
      <div className={styles.czGalleryLabel}>Standard Colours</div>
      <div className={styles.czGalleryRow}>{STANDARD_COLOURS.map((hex) => swatch(hex, "Standard"))}</div>
      <div className={styles.czGalleryActions}>
        {eyedropperSupported() && (
          <button type="button" className={styles.czLinkBtn} onClick={onEyedropper}>
            Eyedropper
          </button>
        )}
        <button type="button" className={styles.czLinkBtn} onClick={onMoreColours}>
          More Colours…
        </button>
      </div>
    </fieldset>
  );
}

// ─── Customise screen ──────────────────────────────────────────────────────

/** Which kind of source is being customised — drives the info message and footer (BRD FR-37, FR-48). */
export type CustomiseSourceKind = "brand" | "yours" | "theme" | "upload";

/** The text-format sections a source can be missing (BRD EC-09). */
export type StyleSection = "title" | "body" | "subtitle" | "footnote";

export interface CustomiseAdvanced {
  allowShadows: boolean;
  isAllowGradients: boolean;
  allowRoundedCorners: boolean;
  allowAnimations: boolean;
  compressImages: boolean;
  isFixAlignment: boolean;
  autofitSlideContent: boolean;
}

interface CustomiseScreenProps {
  readonly brand: BrandSpec;
  readonly advanced: CustomiseAdvanced;
  readonly sourceKind: CustomiseSourceKind;
  readonly sourceName: string;
  /** Sections the source doesn't define — expanded and marked NOT SET (FR-38). */
  readonly missingSections?: readonly StyleSection[];
  /** Brand template: keep the changes for this apply only (FR-49). */
  readonly onUseForThisApply: (brand: BrandSpec, advanced: CustomiseAdvanced, added: StyleSection[]) => void;
  /** Deck theme / upload / Your Template: save, overwriting the previous version (FR-50). */
  readonly onSaveChanges: (brand: BrandSpec, advanced: CustomiseAdvanced, added: StyleSection[]) => void;
  /** No changes were made (Done, or Back with nothing to lose). */
  readonly onDone: () => void;
  /** Discards the changes (Cancel, or Discard in the leave dialog) (FR-51). */
  readonly onDiscard: () => void;
}

const SECTION_ORDER = ["colours", "fonts", "title", "body", "subtitle", "footnote", "advanced"] as const;
type SectionKey = (typeof SECTION_ORDER)[number];

const SECTION_TITLES: Record<SectionKey, string> = {
  colours: "Colours",
  fonts: "Fonts",
  title: "Title Format",
  body: "Body Text Format",
  subtitle: "Subtitle Format",
  footnote: "Footnote Format",
  advanced: "Advanced Options",
};

/** Each section's open state is remembered for the rest of the session (FR-38). */
const sectionOpenMemory: Partial<Record<SectionKey, boolean>> = { colours: true };

const THEME_SLOTS: Array<{ key: keyof BrandSpec["colors"]; label: string }> = [
  { key: "dk1", label: "Text/Background - Dark 1" }, { key: "lt1", label: "Text/Background - Light 1" },
  { key: "dk2", label: "Text/Background - Dark 2" }, { key: "lt2", label: "Text/Background - Light 2" },
  { key: "accent1", label: "Accent 1" }, { key: "accent2", label: "Accent 2" },
  { key: "accent3", label: "Accent 3" }, { key: "accent4", label: "Accent 4" },
  { key: "accent5", label: "Accent 5" }, { key: "accent6", label: "Accent 6" },
  { key: "hlink", label: "Hyperlink" }, { key: "folHlink", label: "Followed Hyperlink" },
];

const INFO_MESSAGE: Record<CustomiseSourceKind, (name: string) => string> = {
  brand: () => "Changes are for this apply only. Brand templates are managed by your organisation, so your changes can't be saved to the template.",
  yours: (name) => `Changes update this template. Saving updates ${name} in Your Templates, so your changes are used every time you apply it.`,
  theme: () => "Keep your changes. Save this as a template to reuse your changes later.",
  upload: () => "Keep your changes. Save this as a template to reuse your changes later.",
};

const STANDARD_SIZES = [8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 44, 48, 54, 60, 66, 72, 80, 88, 96];
const MIN_SIZE = 1;
const MAX_SIZE = 400;

/** Next size up/down through the standard sizes 8 to 96, then in steps of 8 (FR-45). */
export function stepSize(current: number, direction: 1 | -1): number {
  let next: number;
  if (direction === 1) {
    next = STANDARD_SIZES.find((s) => s > current) ?? (Math.floor(current / 8) + 1) * 8;
  } else if (current > 96) {
    next = Math.max(96, (Math.ceil(current / 8) - 1) * 8);
  } else {
    next = [...STANDARD_SIZES].reverse().find((s) => s < current) ?? STANDARD_SIZES[0]!;
  }
  return Math.min(MAX_SIZE, Math.max(MIN_SIZE, next));
}

/** Parses a typed size; returns the EC-24 message when it isn't valid. */
export function validateSize(raw: string): { value: number } | { error: string } {
  const text = raw.trim();
  if (text === "" || Number.isNaN(Number(text))) return { error: "Enter a number, like 12." };
  const n = Number(text);
  if (n < MIN_SIZE || n > MAX_SIZE) return { error: "Enter a size from 1 to 400." };
  return { value: n };
}

type FormatKey = "titleFormat" | "bodyTextFormat" | "subtitleFormat" | "footnoteFormat";
const FORMAT_KEY: Record<StyleSection, FormatKey> = {
  title: "titleFormat", body: "bodyTextFormat", subtitle: "subtitleFormat", footnote: "footnoteFormat",
};

/** Small monochrome (currentColor) glyph shown at the left of each Customise
 *  section header — the mock's icon set: palette, "T", "H", text lines for the
 *  paragraph formats, a note card for Footnote and a gear for Advanced Options. */
function SectionIcon({ section }: Readonly<{ section: SectionKey }>) {
  const common = { width: 14, height: 14, viewBox: "0 0 20 20", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (section) {
    case "colours":
      return (
        <svg {...common} aria-hidden="true">
          <path d="M10 2a8 8 0 00-1 15.9c1 .1 2-.8 2-1.9v-.5c0-1 .8-1.5 1.5-1.5a1.5 1.5 0 001.5-1.5A8 8 0 0010 2z" />
          <circle cx="7" cy="8" r="1" fill="currentColor" />
          <circle cx="10" cy="6" r="1" fill="currentColor" />
          <circle cx="13" cy="8" r="1" fill="currentColor" />
        </svg>
      );
    case "fonts":
      return (
        <svg {...common} aria-hidden="true">
          <path d="M4 6V4h12v2" />
          <path d="M10 4v12" />
          <path d="M7.5 16h5" />
        </svg>
      );
    case "title":
      return (
        <svg {...common} aria-hidden="true">
          <path d="M5 4v12" />
          <path d="M15 4v12" />
          <path d="M5 10h10" />
        </svg>
      );
    case "body":
      return (
        <svg {...common} aria-hidden="true">
          <path d="M3 4h14" />
          <path d="M3 8h9" />
          <path d="M3 12h12" />
          <path d="M3 16h7" />
        </svg>
      );
    case "subtitle":
      return (
        <svg {...common} aria-hidden="true">
          <path d="M3 5h14" />
          <path d="M3 10h14" />
          <path d="M3 15h8" />
        </svg>
      );
    case "footnote":
      return (
        <svg {...common} aria-hidden="true">
          <rect x="2.5" y="3.5" width="15" height="13" rx="1.5" />
          <path d="M5.5 13.5h5" />
          <path d="M5.5 7h9" strokeOpacity={0.45} />
          <path d="M5.5 9.5h6" strokeOpacity={0.45} />
        </svg>
      );
    default: // advanced — gear
      return (
        <svg {...common} aria-hidden="true">
          <circle cx="10" cy="10" r="2.5" />
          <path d="M10 1.5l1.2 2.3a6 6 0 012 1.2l2.3-1.2 1.5 2.6-2 1.5a6 6 0 010 2.2l2 1.5-1.5 2.6-2.3-1.2a6 6 0 01-2 1.2L10 18.5l-1.2-2.3a6 6 0 01-2-1.2l-2.3 1.2-1.5-2.6 2-1.5a6 6 0 010-2.2l-2-1.5 1.5-2.6 2.3 1.2a6 6 0 012-1.2z" />
        </svg>
      );
  }
}

/** Chevron that points down when collapsed and flips up when the section is
 *  open (CSS rotation keeps the two states visually consistent). */
function SectionChevron({ open }: Readonly<{ open: boolean }>) {
  return (
    <svg
      className={`${styles.czChevron} ${open ? styles.czChevronOpen : ""}`}
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

/** Bold / Italic / Underline segmented toggle group (BRD FR-45). */
function StyleToggleGroup({
  format,
  onToggle,
}: Readonly<{ format: PlaceholderFormat; onToggle: (patch: Partial<PlaceholderFormat>) => void }>) {
  return (
    <fieldset className={styles.czBtnGroup}>
      <legend className={styles.srOnly}>Text style</legend>
      <button
        type="button"
        className={`${styles.czToggleBtn} ${format.bold ? styles.czToggleOn : ""}`}
        aria-pressed={format.bold ?? false}
        aria-label="Bold"
        onClick={() => onToggle({ bold: !(format.bold ?? false) })}
      >
        <b>B</b>
      </button>
      <button
        type="button"
        className={`${styles.czToggleBtn} ${format.isItalic ? styles.czToggleOn : ""}`}
        aria-pressed={format.isItalic ?? false}
        aria-label="Italic"
        onClick={() => onToggle({ isItalic: !(format.isItalic ?? false) })}
      >
        <i>I</i>
      </button>
      <button
        type="button"
        className={`${styles.czToggleBtn} ${format.isUnderline ? styles.czToggleOn : ""}`}
        aria-pressed={format.isUnderline ?? false}
        aria-label="Underline"
        onClick={() => onToggle({ isUnderline: !(format.isUnderline ?? false) })}
      >
        <u>U</u>
      </button>
    </fieldset>
  );
}

export function CustomiseScreen({
  brand, advanced, sourceKind, sourceName, missingSections = [], onUseForThisApply, onSaveChanges, onDone, onDiscard,
}: Readonly<CustomiseScreenProps>) {
  const [draft, setDraft] = useState<BrandSpec>(brand);
  const [draftAdv, setDraftAdv] = useState<CustomiseAdvanced>(advanced);
  const [fonts, setFonts] = useState<FontOption[]>([]);
  const [touched, setTouched] = useState<ReadonlySet<StyleSection>>(new Set());
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [open, setOpen] = useState<Partial<Record<SectionKey, boolean>>>(() => {
    const next: Partial<Record<SectionKey, boolean>> = { ...sectionOpenMemory };
    for (const s of missingSections) next[s] = true;
    return next;
  });
  const firstMissingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    loadFontData().then((data) => { if (live) setFonts(data.allFonts); }).catch(() => undefined);
    return () => { live = false; };
  }, []);

  // Opened from Add Missing Styles: the first missing section is brought into view.
  useEffect(() => {
    if (missingSections.length > 0) firstMissingRef.current?.scrollIntoView?.({ block: "start" });
  }, []);

  const unsetSections = useMemo(
    () => new Set(missingSections.filter((s) => !touched.has(s))),
    [missingSections, touched],
  );

  const dirty =
    touched.size > 0 || JSON.stringify(draft) !== JSON.stringify(brand) || JSON.stringify(draftAdv) !== JSON.stringify(advanced);

  const toggle = (key: SectionKey) => {
    setOpen((o) => {
      const next = !o[key];
      sectionOpenMemory[key] = next;
      return { ...o, [key]: next };
    });
  };

  const markTouched = useCallback((section: StyleSection) => {
    setTouched((t) => (t.has(section) ? t : new Set(t).add(section)));
  }, []);

  const secondary = draft.secondaryColors ?? [];
  const [picker, setPicker] = useState<
    | { kind: "slot"; key: keyof BrandSpec["colors"]; label: string }
    | { kind: "secondary-add" }
    | { kind: "secondary-edit"; hex: string }
    | { kind: "format"; section: StyleSection }
    | null
  >(null);
  const [gallery, setGallery] = useState<StyleSection | null>(null);
  // The font colour gallery's own Eyedropper button (FR-42) — shows the same
  // hint as the custom picker's while a pick is in progress.
  const [galleryPicking, setGalleryPicking] = useState(false);

  const setSlot = (key: keyof BrandSpec["colors"], hex: string) => {
    setDraft((d) => ({ ...d, colors: { ...d.colors, [key]: hex } }));
  };
  const addSecondary = (hex: string) => {
    // A colour already in the list is not added again (FR-40).
    setDraft((d) => {
      const list = d.secondaryColors ?? [];
      return list.includes(hex) ? d : { ...d, secondaryColors: [...list, hex] };
    });
  };
  const removeSecondary = (hex: string) => {
    setDraft((d) => ({ ...d, secondaryColors: (d.secondaryColors ?? []).filter((c) => c !== hex) }));
  };

  const setFormat = (section: StyleSection, patch: Partial<PlaceholderFormat>) => {
    markTouched(section);
    const key = FORMAT_KEY[section];
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  };
  const setHeadingFont = (name: string) => {
    markTouched("title");
    setDraft((d) => ({ ...d, fonts: { ...d.fonts, titleFont: name }, titleFormat: { ...d.titleFormat, fontName: name } }));
  };
  // The Body font cascades to subtitle/footnote too — brandApplier reads each
  // format's own fontName independently, and Customise exposes one Body control.
  const setBodyFont = (name: string) => {
    setDraft((d) => ({
      ...d,
      fonts: { ...d.fonts, bodyFont: name },
      bodyTextFormat: { ...d.bodyTextFormat, fontName: name },
      subtitleFormat: { ...d.subtitleFormat, fontName: name },
      footnoteFormat: { ...d.footnoteFormat, fontName: name },
    }));
  };

  const missingFonts = useMemo(
    () => findMissingFonts([draft.fonts.titleFont, draft.fonts.bodyFont]),
    [draft.fonts.titleFont, draft.fonts.bodyFont],
  );

  const handleBack = () => {
    if (dirty) setLeaveOpen(true);
    else onDone();
  };

  const added = [...touched];
  const fontSummary =
    draft.fonts.titleFont === draft.fonts.bodyFont ? draft.fonts.titleFont : `${draft.fonts.titleFont} · ${draft.fonts.bodyFont}`;

  const formatSummary = (section: StyleSection) => {
    if (unsetSections.has(section)) return "–";
    const f = draft[FORMAT_KEY[section]];
    const flags = [f.bold && "Bold", f.isItalic && "Italic", f.isUnderline && "Underline"].filter(Boolean).join(", ");
    const base = `${f.fontSize} pt`;
    return flags ? `${base} · ${flags}` : base;
  };

  const summaries: Record<SectionKey, string> = {
    colours: `${secondary.length} secondary`,
    fonts: fontSummary,
    title: formatSummary("title"),
    body: formatSummary("body"),
    subtitle: formatSummary("subtitle"),
    footnote: formatSummary("footnote"),
    advanced: "",
  };

  const renderFormat = (section: StyleSection, withStyleToggles: boolean) => {
    const key = FORMAT_KEY[section];
    const f = draft[key];
    const unset = unsetSections.has(section);
    return (
      <div className={styles.czSectionBody}>
        <div className={styles.czFormatRow}>
          <SizeField
            label="Size"
            value={unset ? null : f.fontSize}
            onChange={(v) => setFormat(section, { fontSize: v })}
          />
          {withStyleToggles && (
            <StyleToggleGroup format={f} onToggle={(patch) => setFormat(section, patch)} />
          )}
          <button
            type="button"
            className={styles.czColourBtnA}
            aria-label={`${SECTION_TITLES[section]} colour`}
            aria-expanded={gallery === section}
            onClick={() => setGallery((g) => (g === section ? null : section))}
          >
            <span className={styles.czColourGlyph}>
              <span className={styles.czColourA}>A</span>
              <span
                className={`${styles.czColourBar} ${unset ? styles.czColourBarUnset : ""}`}
                style={unset ? undefined : { backgroundColor: `#${f.fontColor}` }}
              />
            </span>
            <span className={styles.czColourChevron} aria-hidden="true">⌄</span>
          </button>
        </div>
        {gallery === section && (
          <>
            <FontColourGallery
              colors={draft.colors}
              secondary={secondary}
              value={f.fontColor}
              onPick={(hex) => {
                setFormat(section, { fontColor: hex });
                setGallery(null);
              }}
              onEyedropper={() => {
                setGalleryPicking(true);
                pickColourFromScreen().then((hex) => {
                  setGalleryPicking(false);
                  if (hex) setFormat(section, { fontColor: hex });
                  setGallery(null);
                }).catch(() => { setGalleryPicking(false); setGallery(null); });
              }}
              onMoreColours={() => {
                setGallery(null);
                setPicker({ kind: "format", section });
              }}
            />
            {galleryPicking && <output className={styles.czPickerHint}>Click anywhere to pick a colour. Press Esc to cancel.</output>}
          </>
        )}
      </div>
    );
  };

  const pickerNode = (() => {
    if (!picker) return null;
    const close = () => setPicker(null);
    if (picker.kind === "slot") {
      return (
        <ColourPicker
          title={picker.label}
          value={draft.colors[picker.key] ?? "808080"}
          onCancel={close}
          onApply={(hex) => { setSlot(picker.key, hex); close(); }}
        />
      );
    }
    if (picker.kind === "secondary-add") {
      return <ColourPicker title="Add secondary colour" value="0B5DAA" mode="add" onCancel={close} onApply={(hex) => { addSecondary(hex); close(); }} />;
    }
    if (picker.kind === "secondary-edit") {
      const mode: PickerMode = "existing-secondary";
      return (
        <ColourPicker
          title="Secondary colour"
          value={picker.hex}
          mode={mode}
          onCancel={close}
          onRemove={() => { removeSecondary(picker.hex); close(); }}
          onApply={(hex) => {
            setDraft((d) => ({ ...d, secondaryColors: (d.secondaryColors ?? []).map((c) => (c === picker.hex ? hex : c)) }));
            close();
          }}
        />
      );
    }
    return (
      <ColourPicker
        title="More colours"
        value={draft[FORMAT_KEY[picker.section]].fontColor}
        onCancel={close}
        onApply={(hex) => { setFormat(picker.section, { fontColor: hex }); close(); }}
      />
    );
  })();

  const sectionBody = (key: SectionKey) => {
    switch (key) {
      case "colours":
        return (
          <div className={styles.czSectionBody}>
            <div className={styles.czGalleryLabel}>Theme Colours</div>
            <div className={styles.czSlotGrid}>
              {THEME_SLOTS.map(({ key: slot, label }) => {
                const hex = normalizeHex(draft.colors[slot] ?? undefined) ?? "808080";
                return (
                  <button
                    key={slot}
                    type="button"
                    className={styles.czSlot}
                    aria-label={`${label} #${hex}`}
                    onClick={() => setPicker({ kind: "slot", key: slot, label })}
                  >
                    <span className={styles.czSlotSwatch} style={{ backgroundColor: `#${hex}` }} />
                    <SlotChevron />
                  </button>
                );
              })}
            </div>
            <div className={styles.czGalleryLabel}>Secondary Colours</div>
            <div className={styles.czSlotGrid}>
              {secondary.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  className={styles.czSlot}
                  aria-label={`Secondary colour #${hex}`}
                  onClick={() => setPicker({ kind: "secondary-edit", hex })}
                >
                  <span className={styles.czSlotSwatch} style={{ backgroundColor: `#${hex}` }} />
                  <SlotChevron />
                </button>
              ))}
              <button
                type="button"
                className={`${styles.czSlot} ${styles.czSlotAdd}`}
                aria-label="Add secondary colour"
                onClick={() => setPicker({ kind: "secondary-add" })}
              >
                <svg aria-hidden="true" viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 10h12M10 4v12" />
                </svg>
              </button>
            </div>
            {sourceKind === "brand" && (
              <p className={styles.czHint}>Changes to brand template colours are for this apply only.</p>
            )}
            {pickerNode}
          </div>
        );
      case "fonts":
        return (
          <div className={styles.czSectionBody}>
            <FontCombobox
              label="Heading"
              value={draft.fonts.titleFont}
              fonts={fonts}
              themeFonts={[{ label: "Headings", name: draft.fonts.titleFont }, { label: "Body", name: draft.fonts.bodyFont }]}
              onChange={setHeadingFont}
            />
            <FontCombobox
              label="Body"
              value={draft.fonts.bodyFont}
              fonts={fonts}
              themeFonts={[{ label: "Headings", name: draft.fonts.titleFont }, { label: "Body", name: draft.fonts.bodyFont }]}
              onChange={setBodyFont}
            />
            {missingFonts.map((font) => (
              <output key={font} className={styles.czWarn}>
                <b>{font}</b> isn&rsquo;t installed on this device. PowerPoint will show a similar font instead, so text may look
                different. Install the font, or choose another one.
              </output>
            ))}
          </div>
        );
      case "title":
        return renderFormat("title", true);
      case "body":
        return renderFormat("body", false);
      case "subtitle":
        return renderFormat("subtitle", true);
      case "footnote":
        return renderFormat("footnote", true);
      default:
        return (
          <div className={styles.czSectionBody}>
            <div className={styles.czOptGroupLabel}>VISUAL EFFECTS</div>
            {([
              ["allowRoundedCorners", "Keep rounded corners"],
              ["allowShadows", "Keep shadows"],
              ["allowAnimations", "Keep animations"],
            ] as const).map(([k, label]) => (
              <label key={k} className={styles.czSwitchRow}>
                <span>{label}</span>
                <input
                  type="checkbox"
                  role="switch"
                  className={styles.czSwitch}
                  checked={draftAdv[k]}
                  onChange={(e) => setDraftAdv((a) => ({ ...a, [k]: e.target.checked }))}
                />
              </label>
            ))}
          </div>
        );
    }
  };

  const footer = !dirty ? (
    <button type="button" className={styles.applyBtn} onClick={onDone}>Done</button>
  ) : (
    <div className={styles.czFooterRow}>
      <button
        type="button"
        className={styles.applyBtn}
        onClick={() =>
          sourceKind === "brand"
            ? onUseForThisApply(draft, draftAdv, added)
            : onSaveChanges(draft, draftAdv, added)
        }
      >
        {sourceKind === "brand" ? "Use for This Apply" : "Save Changes"}
      </button>
      <button type="button" className={styles.czCancelBtn} onClick={onDiscard}>Cancel</button>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader title="Customise" subtitle="Deckorate" onBack={handleBack} />
        <output className={styles.czInfo}>{INFO_MESSAGE[sourceKind](sourceName)}</output>

        {SECTION_ORDER.map((key) => {
          const isOpen = Boolean(open[key]);
          const missing = unsetSections.has(key as StyleSection);
          const isFirstMissing = missingSections.length > 0 && key === missingSections[0];
          const fontWarn = key === "fonts" && missingFonts.length > 0;
          return (
            <div key={key} className={styles.czSection} ref={isFirstMissing ? firstMissingRef : undefined}>
              <button
                type="button"
                className={styles.czSectionHeader}
                aria-expanded={isOpen}
                aria-controls={`customise-${key}`}
                onClick={() => toggle(key)}
              >
                <span className={styles.czSectionIcon} aria-hidden="true">
                  <SectionIcon section={key} />
                </span>
                <span className={styles.czSectionTitle}>{SECTION_TITLES[key]}</span>
                {fontWarn && (
                  <span className={styles.czWarnIcon}>
                    <span aria-hidden="true">⚠</span>
                    <span className={styles.srOnly}>Font not installed</span>
                  </span>
                )}
                {missing && <span className={styles.czNotSet}>NOT SET</span>}
                <span className={styles.czSectionSummary}>{summaries[key]}</span>
                <SectionChevron open={isOpen} />
              </button>
              {isOpen && <div id={`customise-${key}`}>{sectionBody(key)}</div>}
            </div>
          );
        })}
      </div>

      <div className={styles.applyBar}>{footer}</div>

      {leaveOpen && (
        <ConfirmDialog
          title="Discard your changes?"
          safeLabel="Keep Editing"
          confirmLabel="Discard"
          isDanger
          onSafe={() => setLeaveOpen(false)}
          onConfirm={() => { setLeaveOpen(false); onDiscard(); }}
        >
          Your changes to <b>{sourceName}</b> haven&rsquo;t been saved yet.
        </ConfirmDialog>
      )}
    </div>
  );
}

/** Numeric font size field (BRD FR-45): 1 to 400 pt, steppers and arrow keys
 *  through the standard sizes. "–" while unset. An invalid value is rejected
 *  with its message and the field returns to the previous value (EC-24). */
function SizeField({ label, value, onChange }: Readonly<{ label: string; value: number | null; onChange: (v: number) => void }>) {
  const [text, setText] = useState(value === null ? "–" : String(value));
  const [error, setError] = useState<string | null>(null);
  const inputId = useId();

  useEffect(() => {
    setText(value === null ? "–" : String(value));
  }, [value]);

  const commit = () => {
    const result = validateSize(text);
    if ("error" in result) {
      setError(result.error);
      setText(value === null ? "–" : String(value));
      return;
    }
    setError(null);
    if (result.value !== value) onChange(result.value);
  };

  const step = (direction: 1 | -1) => {
    const base = value ?? (direction === 1 ? 7 : 9);
    const next = stepSize(base, direction);
    setError(null);
    setText(String(next));
    onChange(next);
  };

  return (
    <span className={styles.czSizeField}>
      <label className={styles.czSizeLabel} htmlFor={inputId}>{label}</label>
      <span className={styles.czSizeBox}>
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          maxLength={3}
          className={styles.czSizeInput}
          aria-label={`${label} (pt)`}
          aria-invalid={error !== null}
          value={text}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => { setText(e.target.value); setError(null); }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") { e.preventDefault(); step(1); }
            else if (e.key === "ArrowDown") { e.preventDefault(); step(-1); }
            else if (e.key === "Enter") { e.preventDefault(); commit(); }
          }}
        />
        {/* `.czSizeBox` is a flex row with `gap`, so no literal space belongs
            here (typescript:S6772). */}
        <span className={styles.czStepperCol}>
          <button type="button" className={styles.czStepperBtn} aria-label={`Increase ${label.toLowerCase()}`} onClick={() => step(1)}>▲</button>
          <button type="button" className={styles.czStepperBtn} aria-label={`Decrease ${label.toLowerCase()}`} onClick={() => step(-1)}>▼</button>
        </span>
      </span>
      <span className={styles.czUnit}>pt</span>
      {error && <span className={styles.czFieldError} role="alert">{error}</span>}
    </span>
  );
}

// ─── Reading checklist ─────────────────────────────────────────────────────

/** Reading-progress checklist shown while an uploaded template is being
 * parsed (BRD FR-28) — the extractor itself is a single async call with no
 * granular phase signal, so these steps advance on a timer purely to give the
 * user a sense of progress; they all resolve to "done" once reading finishes. */
export const READING_STEPS = [
  "Opening the file",
  "Finding theme colours",
  "Reading fonts",
  "Checking text styles",
  "Building a preview",
];

export function ReadingChecklist({ filename }: Readonly<{ filename: string }>) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setStepIndex(0);
    const id = setInterval(() => {
      setStepIndex((i) => (i < READING_STEPS.length - 1 ? i + 1 : i));
    }, 550);
    return () => clearInterval(id);
  }, [filename]);

  return (
    <div className={styles.extractChecklist}>
      <div className={styles.extractChecklistTitle}>Reading {filename}…</div>
      <ul className={styles.extractChecklistList}>
        {READING_STEPS.map((label, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          let icon = "○";
          if (done) icon = "✓";
          else if (active) icon = "●";
          return (
            <li
              key={label}
              className={[
                styles.extractChecklistItem,
                done ? styles.extractChecklistDone : "",
                active ? styles.extractChecklistActive : "",
              ].filter(Boolean).join(" ")}
            >
              <span className={styles.extractChecklistIcon} aria-hidden="true">
                {icon}
              </span>
              {label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── This Deck's Theme tab ─────────────────────────────────────────────────

export type DeckLoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; masters: readonly MasterInfo[] };

interface ThemeTabProps {
  readonly load: DeckLoadState;
  readonly selectedMasterId: string | null;
  readonly scope: "slide" | "deck";
  readonly removeOthers: boolean;
  readonly onRemoveOthersChange: (value: boolean) => void;
  readonly savedMasterIds: ReadonlySet<string>;
  readonly appliedMasterId: string | null;
  readonly onSelectMaster: (master: MasterInfo) => void;
  readonly onCustomise: (master: MasterInfo) => void;
  readonly onSave: (master: MasterInfo) => void;
  readonly onBrowseTemplates: () => void;
  /** Notes rendered under the selected master, narrowed to the active view's
   *  focus; `sourceName` is how the deck is named in those notes. */
  readonly renderNotes: (master: MasterInfo, sourceName: string, focus: ThemeFocus) => ReactNode;
  readonly disabled: boolean;
  /** The selected single master is missing brand styles or installed fonts
   *  (BRD EC-09, EC-11) — takes priority over the structural single/multi view. */
  readonly hasMissingStyles: boolean;
  readonly hasMissingFonts: boolean;
}

/** The five views in the design's row. Structural (one/multi/office) comes from
 *  the real master count; missing/fonts filter to those warnings. */
type ThemeState = "single" | "multi" | "missing" | "fonts" | "msdefault";
/** Which notes a view shows under the card (passed through to SourceNotes). */
type ThemeFocus = "styles" | "fonts" | "captured";

const OFFICE_DEFAULT_NOTE = "This deck has no brand theme — it uses the Office default.";

/** The "Prototype:" view row, in the mock's order. */
const THEME_VIEWS: ReadonlyArray<{ key: ThemeState; label: string }> = [
  { key: "single", label: "One master" },
  { key: "multi", label: "Multiple masters" },
  { key: "missing", label: "Missing styles" },
  { key: "fonts", label: "Fonts not installed" },
  { key: "msdefault", label: "Office default" },
];

/** The mock's "Prototype:" row of view links — the active view reads as plain text. */
function ThemeViewRow({ view, onChange }: Readonly<{ view: ThemeState; onChange: (view: ThemeState) => void }>) {
  return (
    <div className={styles.themeViewRow}>
      <span className={styles.themeViewLabel}>Prototype:</span>
      {THEME_VIEWS.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          className={`${styles.themeViewLink} ${view === key ? styles.themeViewLinkActive : ""}`}
          aria-pressed={view === key}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** The deck's view, read live from the real deck (BRD EC-09, EC-11): missing
 *  styles or fonts on the single selected master take priority over the
 *  structural single/multi/office-default state. */
function structuralState(masters: readonly MasterInfo[] | null, hasMissingStyles: boolean, hasMissingFonts: boolean): ThemeState {
  if (!masters || masters.length === 1) {
    if (hasMissingStyles) return "missing";
    if (hasMissingFonts) return "fonts";
    return "single";
  }
  if (masters.length === 0) return "msdefault";
  return "multi";
}

/** Per-master controls + callbacks, shared by the single card and multi list. */
interface ThemeCardCtx {
  readonly selectedMasterId: string | null;
  readonly appliedMasterId: string | null;
  readonly disabled: boolean;
  readonly savedMasterIds: ReadonlySet<string>;
  readonly onSelectMaster: (master: MasterInfo) => void;
  readonly onCustomise: (master: MasterInfo) => void;
  readonly onSave: (master: MasterInfo) => void;
  readonly renderNotes: (master: MasterInfo, sourceName: string, focus: ThemeFocus) => ReactNode;
}

function ThemeSaveButton({ master, ctx }: Readonly<{ master: MasterInfo; ctx: ThemeCardCtx }>) {
  if (ctx.savedMasterIds.has(master.id)) return null;
  return (
    <button type="button" className={styles.sessionCardSaveBtn} disabled={ctx.disabled} title="Save to Your Templates" onClick={() => ctx.onSave(master)}>
      <SaveIcon />Save
    </button>
  );
}

function ThemeCustomiseMenu({ master, ctx }: Readonly<{ master: MasterInfo; ctx: ThemeCardCtx }>) {
  return (
    <MoreMenu
      itemName={master.name}
      disabled={ctx.disabled}
      items={[{ key: "customise", label: "Customise", icon: <CustomiseIcon />, onSelect: () => ctx.onCustomise(master) }]}
    />
  );
}

function MasterDot({ selected }: Readonly<{ selected: boolean }>) {
  return <span className={`${styles.themeCardDot} ${selected ? styles.themeCardDotOn : ""}`} aria-hidden="true"><i /></span>;
}

/** The single-master card + its notes (narrowed to the active view's focus). */
function SingleMasterCard({ master, focus, ctx }: Readonly<{ master: MasterInfo; focus: ThemeFocus; ctx: ThemeCardCtx }>) {
  const selected = ctx.selectedMasterId === master.id;
  return (
    <div className={styles.themeItem}>
      <div className={styles.tplRow}>
        <MasterRadioCard master={master} selected={selected} ctx={ctx}>
          Slide master · {master.layoutCount} layouts
        </MasterRadioCard>
        <ThemeSaveButton master={master} ctx={ctx} />
        <ThemeCustomiseMenu master={master} ctx={ctx} />
      </div>
      {selected && ctx.renderNotes(master, "this presentation", focus)}
      {selected && ctx.appliedMasterId === master.id && <div className={styles.appliedNote}>{APPLIED_NOTE}</div>}
    </div>
  );
}

/** The bordered radio card (dot, name, description); Save and ⋯ sit beside it, as in the mock. */
function MasterRadioCard({ master, selected, ctx, children }: Readonly<{
  master: MasterInfo; selected: boolean; ctx: ThemeCardCtx; children: ReactNode;
}>) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={master.name}
      className={`${styles.themeCard} ${selected ? styles.themeCardSelected : ""}`}
      disabled={ctx.disabled}
      onClick={() => ctx.onSelectMaster(master)}
    >
      <MasterDot selected={selected} />
      <span className={styles.themeCardText}>
        <span className={styles.themeCardTitle} title={master.name}>{truncateName(master.name)}</span>
        <span className={styles.themeCardDesc}>{children}</span>
      </span>
    </button>
  );
}

/** One row in the multi-master list. */
function MultiMasterRow({ master, focus, ctx }: Readonly<{ master: MasterInfo; focus: ThemeFocus; ctx: ThemeCardCtx }>) {
  const selected = ctx.selectedMasterId === master.id;
  return (
    <div className={styles.themeItem}>
      <div className={styles.tplRow}>
        <MasterRadioCard master={master} selected={selected} ctx={ctx}>
          {master.layoutCount} layouts · used on {master.slideCount} slide{master.slideCount === 1 ? "" : "s"}
        </MasterRadioCard>
        {selected && <ThemeSaveButton master={master} ctx={ctx} />}
        <ThemeCustomiseMenu master={master} ctx={ctx} />
      </div>
      {selected && ctx.renderNotes(master, master.name, focus)}
      {selected && ctx.appliedMasterId === master.id && <div className={styles.appliedNote}>{APPLIED_NOTE}</div>}
    </div>
  );
}

/** The multi-master radio list + "remove the other masters" option. */
function MultiMasterList({
  masters, focus, scope, removeOthers, onRemoveOthersChange, ctx,
}: Readonly<{
  masters: readonly MasterInfo[]; focus: ThemeFocus; scope: "slide" | "deck";
  removeOthers: boolean; onRemoveOthersChange: (value: boolean) => void; ctx: ThemeCardCtx;
}>) {
  const selectedMaster = masters.find((m) => m.id === ctx.selectedMasterId) ?? null;
  const [infoClosed, setInfoClosed] = useState(false);
  return (
    <>
      {!infoClosed && (
        <BrandAlert tone="info" title={`This presentation has ${masters.length} slide masters`} onClose={() => setInfoClosed(true)}>
          Choose the one every slide should follow.
        </BrandAlert>
      )}
      <div className={styles.tplGroupLabel}>SLIDE MASTERS</div>
      <div role="radiogroup" aria-label="Slide masters" className={styles.tplList}>
        {masters.map((master) => <MultiMasterRow key={master.id} master={master} focus={focus} ctx={ctx} />)}
      </div>
      {scope === "deck" && selectedMaster && (
        <label className={styles.removeOthers}>
          <input type="checkbox" checked={removeOthers} onChange={(e) => onRemoveOthersChange(e.target.checked)} />
          <span>
            Remove the other masters from this presentation{" "}
            <span className={styles.removeOthersHint}>Slides that use them will switch to {selectedMaster.name}.</span>
          </span>
        </label>
      )}
    </>
  );
}

/** The deck's master card(s) for the structural, missing and font views. */
function MasterCards({
  brand, focus, scope, removeOthers, onRemoveOthersChange, ctx,
}: Readonly<{
  brand: readonly MasterInfo[]; focus: ThemeFocus; scope: "slide" | "deck";
  removeOthers: boolean; onRemoveOthersChange: (value: boolean) => void; ctx: ThemeCardCtx;
}>) {
  return brand.length > 1
    ? <MultiMasterList masters={brand} focus={focus} scope={scope} removeOthers={removeOthers} onRemoveOthersChange={onRemoveOthersChange} ctx={ctx} />
    : <SingleMasterCard master={brand[0]!} focus={focus} ctx={ctx} />;
}

/** A short, honest note for a view the open deck doesn't match. */
function ThemeNote({ children }: Readonly<{ children: ReactNode }>) {
  return <BrandAlert tone="info">{children}</BrandAlert>;
}

function OfficeDefaultNote({ onBrowseTemplates }: Readonly<{ onBrowseTemplates: () => void }>) {
  return (
    <BrandAlert
      tone="info"
      title="This presentation doesn't have an active theme."
      actions={[{ label: "Browse Brand Templates", onClick: onBrowseTemplates }]}
    >
      It uses the default Office Theme, so there&rsquo;s no brand to apply. Choose a brand template, or upload one.
    </BrandAlert>
  );
}

/** What the selected view shows below the row — all from the live deck. One /
 *  Multiple / Office default show the real master card(s) or the no-brand
 *  state; Missing styles / Fonts not installed show the card(s) plus just that
 *  warning; a view the deck doesn't match shows a short note. */
function ThemeViewContent({
  view, brand, scope, removeOthers, onRemoveOthersChange, onBrowseTemplates, ctx,
}: Readonly<{
  view: ThemeState; brand: readonly MasterInfo[]; scope: "slide" | "deck";
  removeOthers: boolean; onRemoveOthersChange: (value: boolean) => void;
  onBrowseTemplates: () => void; ctx: ThemeCardCtx;
}>) {
  const isOffice = brand.length === 0;
  const isMulti = brand.length > 1;
  const cards = (focus: ThemeFocus) => (
    <MasterCards brand={brand} focus={focus} scope={scope} removeOthers={removeOthers} onRemoveOthersChange={onRemoveOthersChange} ctx={ctx} />
  );

  if (view === "msdefault") {
    return isOffice
      ? <OfficeDefaultNote onBrowseTemplates={onBrowseTemplates} />
      : <ThemeNote>This deck has a brand theme, so it isn&rsquo;t using the Office default.</ThemeNote>;
  }
  // Every other view needs a brand master; an Office-default deck has none.
  if (isOffice) return <ThemeNote>{OFFICE_DEFAULT_NOTE}</ThemeNote>;
  if (view === "missing") return cards("styles");
  if (view === "fonts") return cards("fonts");
  // Structural views: show the card(s) when the deck matches, else a short note.
  const matches = view === "multi" ? isMulti : !isMulti;
  if (matches) return cards("captured");
  return <ThemeNote>{isMulti ? "This deck has multiple slide masters." : "This deck has a single slide master."}</ThemeNote>;
}

/** This Deck's Theme tab (BRD section 5.4): the open presentation's own slide
 *  master(s) as the brand source — all read live from the open deck, nothing
 *  hard-coded. The active view (BRD EC-09, EC-11, FR-24/25) comes entirely
 *  from the real deck and the selected master's state, unless a view is picked
 *  from the mock's "Prototype:" row (cleared when the deck's own view changes). */
export function ThemeTab({
  load, selectedMasterId, scope, removeOthers, onRemoveOthersChange, savedMasterIds, appliedMasterId,
  onSelectMaster, onCustomise, onSave, onBrowseTemplates, renderNotes, disabled, hasMissingStyles, hasMissingFonts,
}: Readonly<ThemeTabProps>) {
  const readyMasters = load.status === "ready" ? brandMasters(load.masters) : null;
  const liveView = structuralState(readyMasters, hasMissingStyles, hasMissingFonts);
  // A view picked from the Prototype row; it's dropped whenever the live deck's own view changes.
  const [pickedView, setPickedView] = useState<ThemeState | null>(null);
  useEffect(() => setPickedView(null), [liveView]);
  const activeView = pickedView ?? liveView;

  if (load.status === "loading") {
    return <div className={styles.savedTemplatesStatus}>Reading this deck…</div>;
  }
  if (load.status === "error") {
    return (
      <BrandAlert tone="error" title="Couldn't read this presentation.">
        {load.message}
      </BrandAlert>
    );
  }

  const ctx: ThemeCardCtx = {
    selectedMasterId, appliedMasterId, disabled, savedMasterIds, onSelectMaster, onCustomise, onSave, renderNotes,
  };
  return (
    <>
      <ThemeViewRow view={activeView} onChange={setPickedView} />
      <ThemeViewContent
        view={activeView}
        brand={readyMasters ?? []}
        scope={scope}
        removeOthers={removeOthers}
        onRemoveOthersChange={onRemoveOthersChange}
        onBrowseTemplates={onBrowseTemplates}
        ctx={ctx}
      />
    </>
  );
}

// ─── Upload tab ────────────────────────────────────────────────────────────

/** One uploaded file for this PowerPoint session (BRD FR-30). */
export interface UploadEntry {
  readonly id: string;
  readonly name: string;
  readonly buffer: ArrayBuffer;
  /** Brand themes in the file (the Office default theme is never listed). */
  readonly themes: readonly MasterInfo[];
  /** Swatches of the first theme, for the card. */
  readonly swatches: readonly string[];
}

export interface UploadProblem {
  readonly tone: AlertTone;
  readonly title: string;
  readonly text: string;
  /** Show Try Again, which reads the same file again. */
  readonly isRetry: boolean;
}

export interface DuplicatePrompt {
  readonly name: string;
}

export const uploadKey = (uploadId: string, masterId: string) => `${uploadId}:${masterId}`;

interface UploadTabProps {
  readonly uploads: readonly UploadEntry[];
  readonly selectedKey: string | null;
  readonly appliedKey: string | null;
  readonly reading: string | null;
  readonly problem: UploadProblem | null;
  readonly duplicate: DuplicatePrompt | null;
  readonly savedKeys: ReadonlySet<string>;
  readonly renameRequestId: string | null;
  readonly fileInputRef: RefObject<HTMLInputElement>;
  readonly onFiles: (files: FileList | File[] | null) => void;
  readonly onSelect: (upload: UploadEntry, theme: MasterInfo) => void;
  readonly onCustomise: (upload: UploadEntry, theme: MasterInfo) => void;
  readonly onSave: (upload: UploadEntry, theme: MasterInfo) => void;
  readonly onRemove: (upload: UploadEntry) => void;
  readonly onRename: (id: string, name: string) => "ok" | "duplicate";
  readonly onRenameDone: () => void;
  readonly onRetry: () => void;
  readonly onDismissProblem: () => void;
  readonly onDuplicateChoice: (choice: "cancel" | "keep-both" | "replace") => void;
  readonly renderNotes: (upload: UploadEntry, theme: MasterInfo) => ReactNode;
}

/** Upload tab (BRD section 5.5): drop area, reading progress, errors, and the
 *  list of this session's uploads — each with Save and a More options (⋯) menu. */
export function UploadTab(props: Readonly<UploadTabProps>) {
  const {
    uploads, selectedKey, appliedKey, reading, problem, duplicate, savedKeys, renameRequestId, fileInputRef,
    onFiles, onSelect, onCustomise, onSave, onRemove, onRename, onRenameDone, onRetry, onDismissProblem,
    onDuplicateChoice, renderNotes,
  } = props;
  const busy = reading !== null;
  const [dragging, setDragging] = useState(false);
  // The empty drop area: at the top before anything is uploaded, below the uploaded list afterwards.
  const dropArea = (
    <>
          <button
            type="button"
            className={`${styles.uploadZone} ${dragging ? styles.uploadZoneDrag : ""}`}
            disabled={busy}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              onFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <span className={styles.uploadIcon} aria-hidden="true"><UploadArrowIcon /></span>
            <span className={styles.uploadLabel}>Drop a template here, or browse</span>
            <span className={styles.uploadHint}>A PowerPoint file (.pptx or .potx), up to 50 MB.</span>
          </button>
          <p className={styles.uploadHint}>Your uploads stay on this device. Only you can see and remove them.</p>
      </>
  );

  return (
    <div className={styles.sourcePane}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pptx,.potx,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.presentationml.template"
        aria-label="Upload brand template PowerPoint file"
        className={styles.uploadInput}
        onChange={(e) => {
          onFiles(e.target.files);
          // Allow choosing the same file again after an error or Cancel.
          e.target.value = "";
        }}
      />

      {uploads.length === 0 && dropArea}

      {uploads.length > 0 && (
        <button
          type="button"
          className={styles.uploadTemplateBtn}
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadArrowIcon />Upload Template
        </button>
      )}

      {reading && <ReadingChecklist filename={reading} />}

      {problem && (
        <BrandAlert
          tone={problem.tone}
          title={problem.title}
          actions={problem.isRetry ? [{ label: "Try Again", onClick: onRetry }] : []}
          onClose={onDismissProblem}
        >
          {problem.text}
        </BrandAlert>
      )}

      {uploads.length > 0 && (
        <>
          <div className={styles.savedTemplatesHeader}>
            <span className={styles.savedTemplatesTitle}>
              <span>UPLOADED TEMPLATES</span>
              <span className={styles.uploadClockIcon} aria-hidden="true"><ClockIcon /></span>
              <span className={styles.savedTemplatesManaged}>available until PowerPoint closes</span>
            </span>
          </div>
          <div role="radiogroup" aria-label="Uploaded templates">
<ul className={styles.savedTemplatesList}>
            {uploads.map((upload) => (
              <UploadRow
                key={upload.id}
                upload={upload}
                busy={busy}
                selectedKey={selectedKey}
                appliedKey={appliedKey}
                savedKeys={savedKeys}
                isRenameRequested={renameRequestId === upload.id}
                onSelect={onSelect}
                onCustomise={onCustomise}
                onSave={onSave}
                onRemove={onRemove}
                onRename={onRename}
                onRenameDone={onRenameDone}
                renderNotes={renderNotes}
              />
            ))}
          </ul>
</div>
        </>
      )}

      {duplicate && (
        <ConfirmDialog
          title="This file is already uploaded."
          safeLabel="Cancel"
          confirmLabel="Replace"
          extraActions={[{ label: "Keep Both", onClick: () => onDuplicateChoice("keep-both") }]}
          onSafe={() => onDuplicateChoice("cancel")}
          onConfirm={() => onDuplicateChoice("replace")}
        >
          <b>{duplicate.name}</b>
          <br />
          Replace the one in your uploads, or keep both.
        </ConfirmDialog>
      )}
    </div>
  );
}

function UploadRow({
  upload, busy, selectedKey, appliedKey, savedKeys, isRenameRequested, onSelect, onCustomise, onSave, onRemove,
  onRename, onRenameDone, renderNotes,
}: Readonly<{
  upload: UploadEntry;
  busy: boolean;
  selectedKey: string | null;
  appliedKey: string | null;
  savedKeys: ReadonlySet<string>;
  isRenameRequested: boolean;
  onSelect: (upload: UploadEntry, theme: MasterInfo) => void;
  onCustomise: (upload: UploadEntry, theme: MasterInfo) => void;
  onSave: (upload: UploadEntry, theme: MasterInfo) => void;
  onRemove: (upload: UploadEntry) => void;
  onRename: (id: string, name: string) => "ok" | "duplicate";
  onRenameDone: () => void;
  renderNotes: (upload: UploadEntry, theme: MasterInfo) => ReactNode;
}>) {
  const multi = upload.themes.length > 1;
  const first = upload.themes[0]!;
  const selectedTheme = upload.themes.find((t) => selectedKey === uploadKey(upload.id, t.id)) ?? null;
  const selected = selectedTheme !== null;
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(upload.name);
  const [duplicateName, setDuplicateName] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenameRequested) {
      setDraft(upload.name);
      setDuplicateName(false);
      setRenaming(true);
    }
  }, [isRenameRequested]);

  useEffect(() => {
    if (!renaming) return;
    const input = inputRef.current;
    input?.focus();
    input?.setSelectionRange(0, upload.name.lastIndexOf(".") > 0 ? upload.name.lastIndexOf(".") : upload.name.length);
  }, [renaming]);

  const commitRename = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === upload.name) {
      setRenaming(false);
      onRenameDone();
      return;
    }
    if (onRename(upload.id, trimmed) === "duplicate") {
      setDuplicateName(true);
      return;
    }
    setRenaming(false);
    onRenameDone();
  };

  return (
    <li className={`${styles.savedTemplateRow} ${selected ? styles.savedTemplateRowSelected : ""}`}>
      <div className={styles.savedTemplateRowTop}>
        {renaming ? (
          <input
            ref={inputRef}
            type="text"
            className={`${styles.renameInput} ${duplicateName ? styles.renameInputInvalid : ""}`}
            aria-label={`Rename ${upload.name}`}
            aria-invalid={duplicateName}
            maxLength={80}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDuplicateName(false);
            }}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitRename();
              } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setRenaming(false);
                onRenameDone();
              }
            }}
          />
        ) : (
          <label className={styles.savedTemplateRowMain} title={upload.name}>
            <input
              type="radio"
              name="uploaded-template"
              className={styles.savedTemplateRadio}
              checked={selected}
              disabled={busy}
              onChange={() => onSelect(upload, selectedTheme ?? first)}
            />
            {/* `.savedTemplateRowMain` is a flex row with `gap`, so no literal
                space belongs here (typescript:S6772). */}
            <span className={styles.savedTemplateName}>{truncateName(upload.name)}</span>
            {upload.swatches.length > 0 && !multi && (
              <span className={styles.themeCardSwatches}>
                {upload.swatches.map((hex) => (
                  <span key={hex} className={styles.themeCardSwatch} style={{ backgroundColor: `#${hex}` }} title={`#${hex}`} />
                ))}
              </span>
            )}
          </label>
        )}
        {!multi && !savedKeys.has(uploadKey(upload.id, first.id)) && (
          <button type="button" className={styles.sessionCardSaveBtn} disabled={busy} title="Save to Your Templates" onClick={() => onSave(upload, first)}>
            <SaveIcon />Save
          </button>
        )}
        <MoreMenu
          itemName={upload.name}
          disabled={busy}
          items={[
            // A multi-theme file's own menu has Remove from uploads only; each theme has its own Customise.
            ...(multi ? [] : [{ key: "customise", label: "Customise", onSelect: () => onCustomise(upload, first) }]),
            { key: "remove", label: "Remove from uploads", onSelect: () => onRemove(upload) },
          ]}
        />
      </div>
      {renaming && duplicateName && (
        <div className={styles.fieldError} role="alert">
          A template with this name already exists. Enter a different name.
        </div>
      )}
      {multi && <div className={styles.uploadHint}>Contains {upload.themes.length} themes</div>}

      {selected && multi && (
        <div className={styles.themeList}>
          <div className={styles.uploadHint}>Choose a theme to use. Save any theme to keep it.</div>
          <div role="radiogroup" aria-label={`Themes in ${upload.name}`}>
            {upload.themes.map((theme) => {
              const on = selectedKey === uploadKey(upload.id, theme.id);
              return (
                <div key={theme.id} className={styles.savedTemplateRowTop}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-label={theme.name}
                    className={styles.cardRadioButton}
                    disabled={busy}
                    onClick={() => onSelect(upload, theme)}
                  >
                    <span className={`${styles.themeCardDot} ${on ? styles.themeCardDotOn : ""}`} aria-hidden="true"><i /></span>
                    <span className={styles.themeCardTitle} title={theme.name}>{truncateName(theme.name)}</span>
                  </button>
                  {!savedKeys.has(uploadKey(upload.id, theme.id)) && (
                    <button type="button" className={styles.sessionCardSaveBtn} disabled={busy} title="Save to Your Templates" onClick={() => onSave(upload, theme)}>
                      <SaveIcon />Save
                    </button>
                  )}
                  <MoreMenu
                    itemName={theme.name}
                    disabled={busy}
                    items={[{ key: "customise", label: "Customise", onSelect: () => onCustomise(upload, theme) }]}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedTheme && renderNotes(upload, selectedTheme)}
      {selectedTheme && appliedKey === selectedKey && <div className={styles.appliedNote}>{APPLIED_NOTE}</div>}
    </li>
  );
}

// ─── Apply bar ─────────────────────────────────────────────────────────────

export type Scope = "slide" | "deck";
export type SaveTarget = "new" | "current";

export interface BarNotice {
  readonly tone: AlertTone;
  readonly text: string;
  readonly title?: string;
  readonly actions?: readonly { label: string; onClick: () => void }[];
}

/** The Apply button's label (BRD FR-54): varies by scope/destination, and
 *  becomes "Try Again" after a failed apply. */
export function getApplyLabel(scope: Scope, saveTarget: SaveTarget, failed: boolean): string {
  if (failed) return "Try Again";
  if (scope === "slide") return "Apply to This Slide";
  return saveTarget === "new" ? "Apply to a Copy" : "Apply to Presentation";
}

interface ApplyBarProps {
  readonly scope: Scope;
  readonly onScopeChange: (scope: Scope) => void;
  readonly saveTarget: SaveTarget;
  readonly onSaveTargetChange: (target: SaveTarget) => void;
  readonly sourceName: string;
  readonly applyState: ApplyState;
  readonly onCancel: () => void;
  readonly onApply: () => void;
  readonly disabled: boolean;
  readonly disabledReason: string | undefined;
  readonly notice: BarNotice | null;
  readonly onCloseNotice: () => void;
  /** Text appended to the success message for styles that stayed as they were (FR-62). */
  readonly keptStylesText: string | null;
  /** Shown above the Apply button (a brand template's fonts aren't installed — EC-12). */
  readonly aboveButton?: ReactNode;
  /** Lines for the report: options that made the apply skip something (FR-66). */
  readonly skippedActions: readonly string[];
  /** FR-63: re-apply brand to specific slides (skipped EC-31 / changed EC-32). */
  readonly onApplyToSlides?: (indices: readonly number[], total: number) => void;
  /** Set once the shown result came from "Apply to These Slides" (FR-63, AC3). */
  readonly reapplyTotal: number | null;
}

/** The docked apply bar (BRD sections 5.7 and 5.8): scope, destination,
 *  progress with Cancel, success / error / cancelled messages with the report,
 *  and the Apply button. */
export function ApplyBar(props: Readonly<ApplyBarProps>) {
  const {
    scope, onScopeChange, saveTarget, onSaveTargetChange, sourceName, applyState, onCancel, onApply, disabled,
    disabledReason, notice, onCloseNotice, keptStylesText, aboveButton, skippedActions, onApplyToSlides, reapplyTotal,
  } = props;
  const failed = applyState.status === "error";
  const applying = applyState.status === "applying";

  return (
    <div className={styles.applyBar}>
      {notice && (
        <BrandAlert tone={notice.tone} title={notice.title} actions={notice.actions} onClose={onCloseNotice}>
          {notice.text}
        </BrandAlert>
      )}

      {/* "Apply to" scope (BRD FR-53): This Slide, then Whole Presentation. */}
      <div className={styles.seg} role="radiogroup" aria-label="Apply to">
        <button
          type="button"
          className={`${styles.segBtn} ${scope === "slide" ? styles.segBtnActive : ""}`}
          data-cmd-action="brand-apply-scope-slide"
          role="radio"
          aria-checked={scope === "slide"}
          disabled={applying}
          onClick={() => onScopeChange("slide")}
        >
          This Slide
        </button>
        <button
          type="button"
          className={`${styles.segBtn} ${scope === "deck" ? styles.segBtnActive : ""}`}
          data-cmd-action="brand-apply-scope-deck"
          role="radio"
          aria-checked={scope === "deck"}
          disabled={applying}
          onClick={() => onScopeChange("deck")}
        >
          Whole Presentation
        </button>
      </div>

      {/* "Save to" destination (BRD FR-53) — Whole Presentation only. */}
      {scope === "deck" && (
        <div className={styles.destinationRow} role="radiogroup" aria-label="Save to">
          <label className={`${styles.destinationOption} ${saveTarget === "current" ? styles.destinationOptionActive : ""}`}>
            <input
              type="radio"
              name="apply-save-target"
              checked={saveTarget === "current"}
              disabled={applying}
              onChange={() => onSaveTargetChange("current")}/>
            <span>Update this presentation</span>
          </label>
          <label className={`${styles.destinationOption} ${saveTarget === "new" ? styles.destinationOptionActive : ""}`}>
            <input
              type="radio"
              name="apply-save-target"
              checked={saveTarget === "new"}
              disabled={applying}
              onChange={() => onSaveTargetChange("new")}
            />
            <span>Save as a copy</span>
          </label>
        </div>
      )}

      <ApplyStatus
        applyState={applyState}
        scope={scope}
        saveTarget={saveTarget}
        sourceName={sourceName}
        onCancel={onCancel}
        keptStylesText={keptStylesText}
        skippedActions={skippedActions}
        onApply={onApply}
        onApplyToSlides={onApplyToSlides}
        reapplyTotal={reapplyTotal}
      />

      {aboveButton}

      <button
        type="button"
        className={styles.applyBtn}
        data-cmd-action="brand-apply-confirm"
        onClick={onApply}
        aria-disabled={disabled}
        disabled={disabled}
        title={disabled && !applying ? disabledReason : undefined}
      >
        {applying ? "Applying…" : getApplyLabel(scope, saveTarget, failed)}
      </button>
    </div>
  );
}

function ApplyStatus({
  applyState, scope, saveTarget, sourceName, onCancel, keptStylesText, skippedActions, onApplyToSlides, reapplyTotal,
}: Readonly<{
  applyState: ApplyState;
  scope: Scope;
  saveTarget: SaveTarget;
  sourceName: string;
  onCancel: () => void;
  onApply: () => void;
  keptStylesText: string | null;
  skippedActions: readonly string[];
  onApplyToSlides?: (indices: readonly number[], total: number) => void;
  reapplyTotal: number | null;
}>) {
  if (applyState.status === "applying") {
    const total = scope === "slide" ? 1 : Math.max(1, applyState.total);
    const slide = scope === "slide" ? 1 : Math.min(total, applyState.current + 1);
    const rawPct = applyState.total > 0 ? (applyState.current / applyState.total) * 100 : 0;
    const progressPct = Math.min(100, Math.max(0, Math.round(rawPct)));
    return (
      <div className={styles.progressWrap}>
        <div className={styles.progressRow}>
          <p className={styles.progressMsg} aria-live="polite">
            Applying {sourceName}… slide {slide} of {total} · {applyState.stage}
          </p>
          <button type="button" className={styles.cancelApplyBtn} onClick={onCancel}>
            Cancel
          </button>
        </div>
        <progress className={styles.progressBar} max={100} value={progressPct} aria-label={`Applying ${sourceName}`} />
      </div>
    );
  }

  if (applyState.status === "cancelled") {
    // FR-58, BR-09: one unconditional message — the apply is always all-or-nothing.
    return <BrandAlert tone="info">Apply cancelled – nothing was changed.</BrandAlert>;
  }

  if (applyState.status === "done") {
    return (
      <ApplySuccess
        result={applyState.result}
        scope={scope}
        saveTarget={saveTarget}
        sourceName={sourceName}
        keptStylesText={keptStylesText}
        skippedActions={skippedActions}
        onApplyToSlides={onApplyToSlides}
        reapplyTotal={reapplyTotal}
      />
    );
  }

  if (applyState.status === "error") {
    return <ApplyError state={applyState} />;
  }

  return null;
}

function ApplyError({ state }: Readonly<{ state: Extract<ApplyState, { status: "error" }> }>) {
  switch (state.kind) {
    case "readonly":
      return (
        <BrandAlert tone="error" title="This presentation can't be edited.">
          It&rsquo;s read-only, so nothing was changed. Make it editable in PowerPoint, then try again.
        </BrandAlert>
      );
    case "connection":
      return (
        <BrandAlert tone="error" title="Connection lost.">
          Nothing was changed in your presentation. Check your connection, then try again.
        </BrandAlert>
      );
    case "interrupted":
      return (
        <BrandAlert tone="error" title={`Apply interrupted at slide ${state.slide ?? 1} of ${state.total ?? 1}.`}>
          PowerPoint stopped responding part-way through. Everything was put back the way it was, so nothing has changed.
          Press Apply Brand to try again.
        </BrandAlert>
      );
    default:
      return (
        <BrandAlert tone="error" title="Couldn't apply the brand.">
          {state.message}
        </BrandAlert>
      );
  }
}

/** "Slide 3", "Slides 1 and 6", "Slides 1, 3 and 6" (BRD EC-32 wording). */
function formatSlideListWithAnd(indices: readonly number[]): string {
  if (indices.length === 1) return `Slide ${indices[0]}`;
  if (indices.length === 2) return `Slides ${indices[0]} and ${indices[1]}`;
  return `Slides ${indices.slice(0, -1).join(", ")} and ${indices[indices.length - 1]}`;
}

const pluralSuffix = (n: number): string => (n === 1 ? "" : "s");
const wasWere = (n: number): string => (n === 1 ? "was" : "were");
const itThey = (n: number): string => (n === 1 ? "it" : "they");

/** Picks the title/caption for {@link ApplySuccess} (BRD FR-59 to FR-61,
 *  FR-63, EC-31, EC-32) — split out so the branch-and-wording logic doesn't
 *  count against the component's own cognitive complexity. */
function getApplySuccessMessage(params: {
  isCopy: boolean;
  isSlide: boolean;
  reapplyTotal: number | null;
  slidesProcessed: number;
  sourceName: string;
  affectedTotal: number;
  skippedDuringApply: readonly number[];
  editedAfterApply: readonly number[];
}): { title: string; caption: string } {
  const { isCopy, isSlide, reapplyTotal, slidesProcessed, sourceName, affectedTotal, skippedDuringApply, editedAfterApply } = params;

  if (isCopy) {
    return { title: "Branded copy opened in a new window", caption: "Save it to choose where it goes." };
  }
  if (isSlide) {
    return { title: "This slide now matches your brand", caption: "Your other slides stay as they were." };
  }
  if (reapplyTotal !== null) {
    // AC3: the result of "Apply to These Slides".
    const changed = slidesProcessed;
    const remaining = reapplyTotal - changed;
    return {
      title: "Brand applied",
      caption: `${sourceName} was applied to the ${changed} slide${pluralSuffix(changed)} that changed. The other ${remaining} already matched.`,
    };
  }
  if (skippedDuringApply.length > 0) {
    // EC-31: slides being edited during the apply are skipped.
    const skippedCount = skippedDuringApply.length;
    return {
      title: `${slidesProcessed} of ${affectedTotal} slides now use ${sourceName}.`,
      caption: `${skippedCount} slide${pluralSuffix(skippedCount)} ${wasWere(skippedCount)} being edited, so ${itThey(skippedCount)} ${wasWere(skippedCount)} skipped.`,
    };
  }
  if (editedAfterApply.length > 0) {
    // EC-32: slides edited after the apply updated them.
    return {
      title: `${sourceName} was applied to all ${affectedTotal} slide${pluralSuffix(affectedTotal)}.`,
      caption: `${formatSlideListWithAnd(editedAfterApply)} ${wasWere(editedAfterApply.length)} edited afterwards and may no longer match.`,
    };
  }
  return {
    title: "Brand applied",
    caption: `All ${slidesProcessed} slide${pluralSuffix(slidesProcessed)} now use ${sourceName}.`,
  };
}

/** Success message (BRD FR-59 to FR-61): this slide / presentation / copy, the
 *  styles that were kept, View Report, and ✕. EC-31/EC-32 notices with re-apply. */
function ApplySuccess({
  result, scope, saveTarget, sourceName, keptStylesText, skippedActions, onApplyToSlides, reapplyTotal,
}: Readonly<{
  result: ApplyBrandResult;
  scope: Scope;
  saveTarget: SaveTarget;
  sourceName: string;
  keptStylesText: string | null;
  skippedActions: readonly string[];
  onApplyToSlides?: (indices: readonly number[], total: number) => void;
  /** Set when this result came from "Apply to These Slides" (FR-63, AC3) — the
   *  total slide count of the deck it was re-applied against. */
  reapplyTotal: number | null;
}>) {
  const [dismissed, setDismissed] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const isCopy = scope === "deck" && saveTarget === "new";
  const isSlide = scope === "slide";

  // A fresh apply always produces a new `result` object, so resetting on that
  // reference re-opens the message for the next apply.
  useEffect(() => {
    setDismissed(false);
    setReportOpen(false);
  }, [result]);

  const handleViewSlides = useCallback(() => {
    setDismissed(true);
    const target = scope === "deck" ? 1 : result.changedSlideIndex;
    if (target) goToSlide(target).catch((e) => logOfficeError(e, "apply-brand-view-slides"));
  }, [scope, result.changedSlideIndex]);

  // EC-31: slides that were being edited during the apply and were skipped.
  // EC-32: slides that were edited after the apply completed.
  const skippedDuringApply = result.skippedSlideIndices ?? [];
  const editedAfterApply = result.editedAfterApplySlideIndices ?? [];
  const reapplyIndices: readonly number[] = [
    ...new Set([...skippedDuringApply, ...editedAfterApply]),
  ];
  // The deck's total slide count behind whichever EC-31/EC-32 message is
  // showing — skipped slides weren't processed, so they still count toward
  // the total; edited-afterward slides were all processed.
  const affectedTotal = skippedDuringApply.length > 0
    ? result.slidesProcessed + skippedDuringApply.length
    : result.slidesProcessed;

  const handleApplyToThese = useCallback(() => {
    if (onApplyToSlides && reapplyIndices.length > 0) {
      onApplyToSlides(reapplyIndices, affectedTotal);
    }
  }, [onApplyToSlides, reapplyIndices, affectedTotal]);

  if (dismissed) return null;

  // FR-59 (this slide) / FR-60 (whole presentation) / FR-61 (copy) / FR-63 (re-apply, EC-31, EC-32).
  const { title, caption } = getApplySuccessMessage({
    isCopy,
    isSlide,
    reapplyTotal,
    slidesProcessed: result.slidesProcessed,
    sourceName,
    affectedTotal,
    skippedDuringApply,
    editedAfterApply,
  });

  return (
    <>
      <div className={`${styles.resultBox} ${styles.resultSuccess}`}>
        <div className={styles.resultHeader}>
          <span className={styles.resultHeaderTitle}>
            <span className={styles.resultHeaderIcon} aria-hidden="true"><AlertIcon tone="success" /></span>
            {title}
          </span>
          <button type="button" className={styles.resultDismiss} aria-label="Dismiss message" onClick={() => setDismissed(true)}>
            ×
          </button>
        </div>
        <p className={styles.resultCaption}>
          {caption}
          {keptStylesText ? ` ${keptStylesText}` : ""}
        </p>
        {result.actions.length > 0 && (
          <ul className={styles.resultActions}>
            {result.actions.map((a) => <li key={a}>{a}</li>)}
          </ul>
        )}
        <div className={styles.resultFooter}>
          {!isCopy && (
            <button type="button" className={styles.viewSlidesBtn} onClick={handleViewSlides}>
              View the Slides
            </button>
          )}
          {/* FR-63: re-apply to only the affected slides. */}
          {reapplyIndices.length > 0 && onApplyToSlides && (
            <button type="button" className={styles.applyToSlidesBtn} onClick={handleApplyToThese}>
              Apply to These Slides
            </button>
          )}
          <button type="button" className={styles.viewReportLink} onClick={() => setReportOpen(true)}>
            View Report
          </button>
        </div>
      </div>
      {reportOpen && <BrandReportModal result={result} skipped={skippedActions} onClose={() => setReportOpen(false)} />}
    </>
  );
}

/** Brand Application Report (BRD FR-66): counts, a ticked list of the actions
 *  taken and any actions skipped by an option, closed with Got it. */
function BrandReportModal({ result, skipped, onClose }: Readonly<{ result: ApplyBrandResult; skipped: readonly string[]; onClose: () => void }>) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Native <dialog> owns focus trapping, Escape-to-close and the modal a11y
  // tree for free — the "close" event (fired for both Escape and
  // dialog.close()) is the single place we sync back to React state. Some
  // older Office WebView hosts (and jsdom under test) don't implement
  // showModal()/close(), so fall back to the plain `open` attribute there.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
    dialog.addEventListener("close", onClose);
    return () => dialog.removeEventListener("close", onClose);
  }, [onClose]);

  const closeDialog = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
      onClose();
    }
  }, [onClose]);

  return (
    <dialog ref={dialogRef} className={styles.reportModal} aria-label="Brand Application Report">
      <div className={styles.reportHeader}>
        <span className={styles.reportTitle}>Brand Application Report</span>
        <button type="button" className={styles.resultDismiss} aria-label="Close report" onClick={closeDialog}>
          ×
        </button>
      </div>
      <p className={styles.reportSummary}>
        {result.slidesProcessed} slide(s) processed, {result.shapesUpdated} shape(s) updated.
      </p>
      {result.actions.length > 0 ? (
        <ul className={styles.reportList}>
          {result.actions.map((a) => <li key={a}>✓ {a}</li>)}
        </ul>
      ) : (
        <p className={styles.reportEmpty}>No changes were needed — this file already matched the brand.</p>
      )}
      {skipped.length > 0 && (
        <>
          <p className={styles.reportSummary}>Skipped by your options</p>
          <ul className={styles.reportList}>
            {skipped.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </>
      )}
      <button type="button" className={styles.applyBtn} onClick={closeDialog}>
        Got it
      </button>
    </dialog>
  );
}

// ─── Apply Brand settings ──────────────────────────────────────────────────────

const INCLUDED_PARTS: ReadonlyArray<{ name: string; detail: string }> = [
  { name: "Colours", detail: "The brand's colour set: dark, light, accents, links" },
  { name: "Fonts", detail: "Title / body typefaces" },
  { name: "Text styles", detail: "Title / body / subtitle / footnote sizing & colour" },
  { name: "Table styles", detail: "Header fill, header text, banding" },
  { name: "Visual effects", detail: "Shadows, corners, animations aligned to brand rules" },
];

/** The screen behind the header's gear: what every apply covers. Choosing which
 *  parts to include is planned and not available yet. */
export function SettingsScreen({ onBack }: Readonly<{ onBack: () => void }>) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <ModuleHeader title="Apply Brand Settings" onBack={onBack} backLabel="Back" />
        <p className={styles.settingsIntro}>
          Apply Brand applies every part of the brand: colours, fonts, text styles, tables and effects. Choosing which parts
          to include is planned and not available yet.
        </p>
        <div className={styles.settingsLabel}>INCLUDED IN EVERY APPLY</div>
        <ul className={styles.settingsList}>
          {INCLUDED_PARTS.map((part) => (
            <li key={part.name} className={styles.settingsRow}>
              <span>
                <span className={styles.settingsName}>{part.name}</span>
                <span className={styles.settingsDetail}>{part.detail}</span>
              </span>
              <span className={styles.settingsAlways}>Always</span>
            </li>
          ))}
        </ul>
        <div className={styles.settingsNote}>A future release will let you turn individual parts on or off per apply.</div>
      </div>
    </div>
  );
}
